import { clsx, type ClassValue } from 'clsx'
import { twMerge } from 'tailwind-merge'
import {
  APP_SHELL_CAPABILITY_OPTIONS_TTL_MS,
  APP_SHELL_CAPABILITY_PREFERENCES_TTL_MS,
  APP_SHELL_CAPABILITY_PROFILE_TTL_MS,
  APP_SHELL_PUBLIC_APP_CONFIG_TTL_MS,
  APP_SHELL_STORE_BIND_STATUS_TTL_MS,
  createAppShellLocalCacheContext,
  deleteAppShellLocalCache,
  readAppShellLocalCache,
  refreshAppShellLocalCacheMetadata,
  writeAppShellLocalCache,
  type AppShellLocalCacheContext,
  type AppShellLocalCacheResource,
} from './local-cache/appShellCache'
import { clearBrowserLocalCacheUserScope } from './local-cache/cleanup'
import { createIndexedDbLocalCacheDriver } from './local-cache/db'
import { createBrowserLocalCacheNamespace } from './local-cache/keys'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export type PublishErrorPresentation = {
  displayMessage: string
  technicalMessage: string | null
  kind: 'socks_proxy_missing' | 'generic'
}

export type HighRiskConfirmDialogContent = {
  title: string
  message: string
  confirmLabel: string
}

type PhoneValidationErrorKey = 'phoneRequired' | 'phoneInvalid'
type OtpValidationErrorKey = 'invalidCode'
type PasswordValidationErrorKey = 'passwordRequired' | 'passwordTooShort' // pragma: allowlist secret
type PasswordStrengthLevelKey = 'resetStrengthWeak' | 'resetStrengthMedium' | 'resetStrengthStrong' // pragma: allowlist secret
type PasswordRequirementLabelKey = 'required' | 'minLength' // pragma: allowlist secret

export type PasswordRequirementState = {
  key: PasswordRequirementLabelKey
  met: boolean
}

type BuildHighRiskConfirmDialogOptions = {
  actionLabel: string
  targetLabel: string
  irreversibleLabel: string
  titleTemplate: string
  targetPrefix: string
  irreversiblePrefix: string
  continueHint: string
}

type AuthMessageResolver = (key: string, params?: Record<string, string | number>) => string

type ResolvePublishErrorOptions = {
  socksFriendlyMessage: string
  fallbackMessage?: string
}

export type ResolveUserFacingApiErrorMessageOptions = {
  fallbackMessage: string
  preserveBusinessMessages?: Array<string | RegExp>
}

type CoerceNumberWithGuardrailsOptions = {
  rawValue: number | string | null | undefined
  min?: number
  max?: number
  fallback?: number
}

export type NumberGuardrailReason = 'invalid' | 'below_min' | 'above_max'

export type NumberGuardrailResult = {
  value: number
  corrected: boolean
  reason: NumberGuardrailReason | null
  min: number
  max: number | null
}

export const AUTH_PHONE_LENGTH = 11
export const AUTH_OTP_LENGTH = 6
export const AUTH_PASSWORD_MIN_LENGTH = 8

const AUTH_ERROR_CODE_MESSAGE_KEY_MAP: Record<string, string> = {
  TERMS_REQUIRED: 'authErrorTermsRequired',
  INVALID_TOKEN: 'authErrorInvalidToken',
  TOKEN_EXPIRED: 'authErrorTokenExpired',
  TOKEN_REVOKED: 'authErrorTokenRevoked',
  UNAUTHORIZED: 'authErrorUnauthorized',
  FORBIDDEN: 'authErrorForbidden',
  USER_NOT_FOUND: 'authErrorUserNotFound',
  USER_DISABLED: 'authErrorUserDisabled',
  INVALID_CREDENTIALS: 'authErrorInvalidCredentials',
  MOBILE_NUMBER_ILLEGAL: 'authErrorPhoneInvalid',
  ISV_MOBILE_NUMBER_ILLEGAL: 'authErrorPhoneInvalid',
  FREQUENCY_FAIL: 'authErrorSmsRateLimited',
  BIZ_FREQUENCY: 'authErrorSmsRateLimited',
  BUSINESS_LIMIT_CONTROL: 'authErrorSmsDailyLimit',
  ISV_DAY_LIMIT_CONTROL: 'authErrorSmsDailyLimit',
  ISV_BUSINESS_LIMIT_CONTROL: 'authErrorSmsDailyLimit',
  FUNCTION_NOT_OPENED: 'authErrorSmsServiceUnavailable',
  ISV_SMS_TEMPLATE_ILLEGAL: 'authErrorSmsServiceUnavailable',
  ISV_AMOUNT_NOT_ENOUGH: 'authErrorSmsServiceUnavailable'
}

const normalizeAuthErrorCode = (value: string): string =>
  value
    .trim()
    .replace(/[\s.-]+/g, '_')
    .toUpperCase()

const extractAuthErrorCode = (record: Record<string, unknown>): string => {
  const detail = record.detail
  const detailRecord = detail && typeof detail === 'object' ? (detail as Record<string, unknown>) : null
  const candidateCodes = [
    record.code,
    record.error_code,
    record.errorCode,
    detailRecord?.code,
    detailRecord?.error_code,
    detailRecord?.errorCode
  ]
  for (const candidate of candidateCodes) {
    if (typeof candidate === 'string' && candidate.trim().length > 0) {
      return candidate.trim()
    }
  }
  return ''
}

const resolveMappedAuthErrorMessage = (
  code: string,
  fallbackMessage: string,
  resolveMessage?: AuthMessageResolver
): string | null => {
  const normalizedCode = normalizeAuthErrorCode(code)
  const messageKey = AUTH_ERROR_CODE_MESSAGE_KEY_MAP[normalizedCode]
  if (!messageKey) {
    console.warn('[auth] 未映射的认证错误码:', code)
    return fallbackMessage
  }
  if (!resolveMessage) {
    return fallbackMessage
  }
  const translatedMessage = resolveMessage(messageKey)
  if (translatedMessage && translatedMessage !== messageKey) {
    return translatedMessage
  }
  return fallbackMessage
}

const normalizeUnknownErrorText = (value: unknown): string => {
  if (typeof value === 'string') {
    return value.trim()
  }
  if (!value || typeof value !== 'object') {
    return ''
  }
  const data = value as Record<string, unknown>
  const directMessage = data.message
  if (typeof directMessage === 'string') {
    return directMessage.trim()
  }
  const detail = data.detail
  if (typeof detail === 'string') {
    return detail.trim()
  }
  if (detail && typeof detail === 'object') {
    const detailObj = detail as Record<string, unknown>
    if (typeof detailObj.message === 'string') {
      return detailObj.message.trim()
    }
    if (typeof detailObj.error === 'string') {
      return detailObj.error.trim()
    }
  }
  if (typeof data.error === 'string') {
    return data.error.trim()
  }
  return ''
}

const AUTH_TECHNICAL_MESSAGE_KEY_PATTERNS: Array<[RegExp, string]> = [
  [/\binvalid[\s_-]*token\b/i, 'authErrorInvalidToken'],
  [/\btoken[\s_-]*expired\b/i, 'authErrorTokenExpired'],
  [/\btoken[\s_-]*revoked\b/i, 'authErrorTokenRevoked'],
  [/\bunauthorized\b/i, 'authErrorUnauthorized'],
  [/\bforbidden\b/i, 'authErrorForbidden']
]

const API_ERROR_TECHNICAL_PATTERNS: RegExp[] = [
  /\bbackend unreachable\b/i,
  /\binvalid[\s_-]*token\b/i,
  /\bpayload[\s_-]*(missing|required)\b/i,
  /\bunknown error\b/i,
  /\b(task|item|collection|publish|sku)[\s_-]*id\b/i,
  /\b(taskId|itemId|collectionId|publishId|selectedSkuKey|selected_sku_keys?|sku_key)\b/i,
  /\bINVALID[_A-Z0-9-]*\b/,
  /\bmissing[_a-z0-9-]*\b/i,
  /\b(required|missing)\b[^。.!?]*\b(param|parameter|field|argument|args?)\b/i,
  /缺少[^，。]*(参数|字段).*(collection|publish|task|item|sku)/i,
  /\b(traceback|stack trace|exception|sqlalchemy|runtimeerror|valueerror|keyerror|http status|status code)\b/i,
  /\b(request_id|error_code)\b/i,
  /\/api\/v\d\//i,
]

const normalizeApiErrorCandidate = (value: unknown): string => {
  if (typeof value !== 'string') {
    return ''
  }
  return value.trim().replace(/\s+/g, ' ')
}

const collectApiErrorCandidates = (payload: unknown): string[] => {
  const collected: string[] = []
  const seen = new Set<string>()
  const push = (value: unknown) => {
    const normalized = normalizeApiErrorCandidate(value)
    if (!normalized || seen.has(normalized)) {
      return
    }
    seen.add(normalized)
    collected.push(normalized)
  }

  if (typeof payload === 'string') {
    push(payload)
    return collected
  }

  if (payload instanceof Error) {
    push(payload.message)
    return collected
  }

  if (!payload || typeof payload !== 'object') {
    return collected
  }

  const record = payload as Record<string, unknown>
  push(record.detail)
  push(record.message)
  push(record.error)
  push(record.error_message)
  push(record.code)

  const detail = record.detail
  if (detail && typeof detail === 'object') {
    const detailRecord = detail as Record<string, unknown>
    push(detailRecord.message)
    push(detailRecord.error)
    push(detailRecord.error_message)
    push(detailRecord.code)
  }

  return collected
}

const resolveMappedAuthTechnicalMessage = (
  message: string,
  fallbackMessage: string,
  resolveMessage?: AuthMessageResolver
): string | null => {
  for (const [pattern, messageKey] of AUTH_TECHNICAL_MESSAGE_KEY_PATTERNS) {
    if (!pattern.test(message)) {
      continue
    }
    if (!resolveMessage) {
      return fallbackMessage
    }
    const translatedMessage = resolveMessage(messageKey)
    return translatedMessage && translatedMessage !== messageKey ? translatedMessage : fallbackMessage
  }
  return null
}

const matchesBusinessWhitelist = (message: string, whitelist: Array<string | RegExp>): boolean => {
  if (!whitelist.length) {
    return false
  }
  const lowerMessage = message.toLowerCase()
  for (const rule of whitelist) {
    if (typeof rule === 'string') {
      const normalizedRule = rule.trim()
      if (!normalizedRule) {
        continue
      }
      if (message.includes(normalizedRule) || lowerMessage.includes(normalizedRule.toLowerCase())) {
        return true
      }
      continue
    }
    if (rule.global || rule.sticky) {
      rule.lastIndex = 0
    }
    if (rule.test(message)) {
      return true
    }
  }
  return false
}

const isTechnicalApiMessage = (message: string): boolean => {
  const normalized = normalizeApiErrorCandidate(message)
  if (!normalized) {
    return true
  }
  if (normalized.length >= 240) {
    return true
  }
  if (/^[A-Z0-9_]{6,}$/.test(normalized)) {
    return true
  }
  if (/^[a-z0-9_]{6,}$/.test(normalized) && /(?:invalid|missing|error|failed)/i.test(normalized)) {
    return true
  }
  if (/^\{.+\}$/.test(normalized) || /^\[.+\]$/.test(normalized)) {
    return true
  }
  return API_ERROR_TECHNICAL_PATTERNS.some((pattern) => pattern.test(normalized))
}

export const resolveUserFacingApiErrorMessage = (
  payload: unknown,
  options: ResolveUserFacingApiErrorMessageOptions | string
): string => {
  const normalizedOptions = typeof options === 'string'
    ? { fallbackMessage: options }
    : options
  const fallbackMessage = normalizeApiErrorCandidate(normalizedOptions?.fallbackMessage) || '操作失败，请稍后重试。'
  const whitelist = Array.isArray(normalizedOptions?.preserveBusinessMessages)
    ? normalizedOptions.preserveBusinessMessages
    : []

  const candidates = collectApiErrorCandidates(payload)
  for (const candidate of candidates) {
    if (matchesBusinessWhitelist(candidate, whitelist)) {
      return candidate
    }
    if (!isTechnicalApiMessage(candidate)) {
      return candidate
    }
  }
  return fallbackMessage
}

const normalizeGuardrailBoundary = (value: unknown, fallback: number): number => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : fallback
}

export const coerceNumberWithGuardrails = (
  options: CoerceNumberWithGuardrailsOptions
): NumberGuardrailResult => {
  const min = normalizeGuardrailBoundary(options.min, 0)
  const rawMax = normalizeGuardrailBoundary(options.max, Number.NaN)
  const max = Number.isFinite(rawMax) ? Math.max(rawMax, min) : null
  const fallbackRaw = normalizeGuardrailBoundary(options.fallback, min)
  const fallback = max === null
    ? Math.max(fallbackRaw, min)
    : Math.min(max, Math.max(fallbackRaw, min))
  const parsedValue = typeof options.rawValue === 'number'
    ? options.rawValue
    : Number(options.rawValue)

  if (!Number.isFinite(parsedValue)) {
    return {
      value: fallback,
      corrected: true,
      reason: 'invalid',
      min,
      max
    }
  }

  if (parsedValue < min) {
    return {
      value: min,
      corrected: true,
      reason: 'below_min',
      min,
      max
    }
  }

  if (max !== null && parsedValue > max) {
    return {
      value: max,
      corrected: true,
      reason: 'above_max',
      min,
      max
    }
  }

  return {
    value: parsedValue,
    corrected: false,
    reason: null,
    min,
    max
  }
}

export const resolvePublishErrorPresentation = (
  rawError: unknown,
  options: ResolvePublishErrorOptions
): PublishErrorPresentation => {
  const rawText = normalizeUnknownErrorText(rawError)
  const lowered = rawText.toLowerCase()
  const isSocksProxyError = lowered.includes('socksio') || lowered.includes('socks proxy')
  if (isSocksProxyError) {
    return {
      displayMessage: options.socksFriendlyMessage,
      technicalMessage: rawText || null,
      kind: 'socks_proxy_missing'
    }
  }
  const fallback = (options.fallbackMessage || '').trim()
  if (!rawText && fallback) {
    return {
      displayMessage: fallback,
      technicalMessage: null,
      kind: 'generic'
    }
  }
  return {
    displayMessage: rawText || fallback || options.socksFriendlyMessage,
    technicalMessage: rawText && rawText !== fallback ? rawText : null,
    kind: 'generic'
  }
}

export const buildHighRiskConfirmDialog = (
  options: BuildHighRiskConfirmDialogOptions
): HighRiskConfirmDialogContent => {
  const actionLabel = options.actionLabel.trim()
  const targetLabel = options.targetLabel.trim()
  const irreversibleLabel = options.irreversibleLabel.trim()
  const title = options.titleTemplate.replace('{action}', actionLabel)

  return {
    title,
    confirmLabel: actionLabel,
    message: [
      `${options.targetPrefix}${targetLabel}`,
      `${options.irreversiblePrefix}${irreversibleLabel}`,
      options.continueHint.trim()
    ]
      .filter((line) => line.trim().length > 0)
      .join('\n')
  }
}

export const normalizeAuthPhone = (value: string): string => value.replace(/[^\d]+/g, '')

export const getPhoneValidationErrorKey = (value: string): PhoneValidationErrorKey | null => {
  const normalizedPhone = normalizeAuthPhone(value)
  if (!normalizedPhone) {
    return 'phoneRequired'
  }
  if (normalizedPhone.length !== AUTH_PHONE_LENGTH) {
    return 'phoneInvalid'
  }
  return null
}

export const getOtpValidationErrorKey = (value: string): OtpValidationErrorKey | null => {
  const normalizedCode = value.trim()
  if (!normalizedCode || normalizedCode.length !== AUTH_OTP_LENGTH) {
    return 'invalidCode'
  }
  return null
}

export const getPasswordValidationErrorKey = (value: string): PasswordValidationErrorKey | null => {
  if (!value) {
    return 'passwordRequired'
  }
  if (value.length < AUTH_PASSWORD_MIN_LENGTH) {
    return 'passwordTooShort'
  }
  return null
}

export const getPasswordRequirementStates = (value: string): PasswordRequirementState[] => [
  {
    key: 'required',
    met: value.length > 0
  },
  {
    key: 'minLength',
    met: value.length >= AUTH_PASSWORD_MIN_LENGTH
  }
]

export const getPasswordStrengthScore = (value: string): number => {
  let score = 0
  if (value.length >= AUTH_PASSWORD_MIN_LENGTH) {
    score += 1
  }
  if (/[A-Za-z]/.test(value)) {
    score += 1
  }
  if (/\d/.test(value)) {
    score += 1
  }
  if (/[^A-Za-z0-9]/.test(value)) {
    score += 1
  }
  return Math.min(score, 4)
}

export const getPasswordStrengthLevelKey = (score: number): PasswordStrengthLevelKey => {
  if (score >= 3) {
    return 'resetStrengthStrong'
  }
  if (score >= 2) {
    return 'resetStrengthMedium'
  }
  return 'resetStrengthWeak'
}

export const resolveAuthApiErrorMessage = (
  payload: unknown,
  fallbackMessage: string,
  resolveMessage?: AuthMessageResolver
): string => {
  if (!payload || typeof payload !== 'object') {
    return fallbackMessage
  }

  const record = payload as Record<string, unknown>
  const code = extractAuthErrorCode(record)
  if (code) {
    return resolveMappedAuthErrorMessage(code, fallbackMessage, resolveMessage) ?? fallbackMessage
  }
  const detail = record.detail
  if (typeof detail === 'string') {
    return resolveMappedAuthTechnicalMessage(detail, fallbackMessage, resolveMessage) ?? detail
  }
  if (detail && typeof detail === 'object') {
    const detailRecord = detail as Record<string, unknown>
    if (typeof detailRecord.message === 'string') {
      return resolveMappedAuthTechnicalMessage(detailRecord.message, fallbackMessage, resolveMessage) ?? detailRecord.message
    }
    if (typeof detailRecord.error === 'string') {
      return resolveMappedAuthTechnicalMessage(detailRecord.error, fallbackMessage, resolveMessage) ?? detailRecord.error
    }
  }
  if (typeof record.message === 'string') {
    return resolveMappedAuthTechnicalMessage(record.message, fallbackMessage, resolveMessage) ?? record.message
  }
  if (typeof record.error === 'string') {
    return resolveMappedAuthTechnicalMessage(record.error, fallbackMessage, resolveMessage) ?? record.error
  }
  return fallbackMessage
}

export type LogMessagePart = {
  text: string
  highlight: boolean
}

const DEFAULT_LOG_KEYWORDS = ['ERROR', 'CACHE', 'THINK']

export function splitLogMessageParts(message: string, keywords: string[] = DEFAULT_LOG_KEYWORDS): LogMessagePart[] {
  if (!message) {
    return []
  }
  const normalized = String(message)
  if (!keywords.length) {
    return [{ text: normalized, highlight: false }]
  }
  const escaped = keywords.map((keyword) => keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
  const regex = new RegExp(`(${escaped.join('|')})`, 'gi')
  const parts = normalized.split(regex).filter((part) => part.length > 0)
  const loweredKeywords = keywords.map((keyword) => keyword.toLowerCase())
  return parts.map((part) => ({
    text: part,
    highlight: loweredKeywords.includes(part.toLowerCase())
  }))
}

declare global {
  interface Window {
    turnstile?: {
      render: (container: HTMLElement, options: Record<string, unknown>) => string
      reset: (widgetId?: string) => void
      remove: (widgetId?: string) => void
    }
  }
}

let turnstileScriptPromise: Promise<void> | null = null

export function loadTurnstileScript() {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('turnstile script can only load in browser'))
  }

  if (window.turnstile) {
    return Promise.resolve()
  }

  if (turnstileScriptPromise) {
    return turnstileScriptPromise
  }

  turnstileScriptPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector('script[data-turnstile-script="true"]') as HTMLScriptElement | null
    if (existing) {
      if (window.turnstile) {
        resolve()
        return
      }
      existing.addEventListener('load', () => resolve())
      existing.addEventListener('error', () => reject(new Error('turnstile script load failed')))
      return
    }

    const script = document.createElement('script')
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'
    script.async = true
    script.defer = true
    script.dataset.turnstileScript = 'true'
    script.onload = () => resolve()
    script.onerror = () => reject(new Error('turnstile script load failed'))
    document.head.appendChild(script)
  })

  return turnstileScriptPromise
}

export function resolveAuthToken(storage: Pick<Storage, 'getItem'> | null = typeof localStorage === 'undefined' ? null : localStorage) {
  if (!storage?.getItem) {
    return null
  }

  const directKeys = ['token', 'access_token', 'auth_token', 'authToken', 'ozon_token', 'guest_token', 'guestToken']
  for (const key of directKeys) {
    const value = storage.getItem(key)
    if (value) {
      return value
    }
  }

  const authPayload = storage.getItem('auth')
  if (authPayload) {
    try {
      const parsed = JSON.parse(authPayload)
      const token = parsed?.token || parsed?.accessToken || parsed?.access_token
      if (typeof token === 'string' && token) {
        return token
      }
    } catch (error) {
      return null
    }
  }

  return null
}

type ExtensionSendMessage = (
  extensionId: string,
  payload: Record<string, any>,
  cb?: (response?: any) => void
) => void

const EXTENSION_MESSAGE_TIMEOUT_MS = 1500

function getExtensionRuntimeBridge() {
  if (typeof window === 'undefined') {
    return null
  }
  const extensionId = document.documentElement?.getAttribute('data-ozon-erp-extension-id')
  const chromeRuntime = (window as unknown as { chrome?: { runtime?: { sendMessage?: Function } } }).chrome?.runtime
  const sendMessage = chromeRuntime?.sendMessage as ExtensionSendMessage | undefined
  if (!extensionId || !sendMessage) {
    return null
  }
  return {
    extensionId,
    sendMessage,
  }
}

async function sendMessageToExtension(payload: Record<string, any>) {
  const bridge = getExtensionRuntimeBridge()
  if (!bridge) {
    return null
  }

  return await new Promise<any | null>((resolve) => {
    let completed = false
    const timerApi = globalThis
    const finish = (value: any | null) => {
      if (completed) {
        return
      }
      completed = true
      timerApi.clearTimeout(timeoutId)
      resolve(value)
    }

    const timeoutId = timerApi.setTimeout(() => {
      finish(null)
    }, EXTENSION_MESSAGE_TIMEOUT_MS)

    try {
      bridge.sendMessage(bridge.extensionId, payload, (response) => {
        const runtimeError = (window as unknown as { chrome?: { runtime?: { lastError?: unknown } } }).chrome?.runtime
          ?.lastError
        if (runtimeError) {
          finish(null)
          return
        }
        finish(response ?? null)
      })
    } catch (error) {
      finish(null)
    }
  })
}

export async function syncAuthToExtension(
  token: string,
  user: Record<string, any> | null,
  apiBaseUrl?: string
) {
  if (!token || typeof window === 'undefined') {
    return false
  }

  const apiUrl = resolveApiBaseUrl(apiBaseUrl)
  const response = await sendMessageToExtension({
    action: 'syncAuth',
    token,
    user,
    apiUrl,
  })

  if (response?.success) {
    window.dispatchEvent(new Event('extension:refresh-status'))
    window.postMessage({ type: 'OZON_ERP_REQUEST_STATUS' }, '*')
    return true
  }

  return false
}

export async function logoutExtensionSession() {
  if (typeof window === 'undefined') {
    return false
  }

  const response = await sendMessageToExtension({ action: 'logout' })
  if (response?.success) {
    window.dispatchEvent(new Event('extension:refresh-status'))
    window.postMessage({ type: 'OZON_ERP_REQUEST_STATUS' }, '*')
    return true
  }
  return false
}

export async function requestExtensionStatus() {
  if (typeof window === 'undefined') {
    return null
  }

  const response = await sendMessageToExtension({ action: 'getStatus' })
  if (!response || response.success === false) {
    return null
  }
  return response
}

export async function wakeRemoteCollectQueuePolling(reason = 'server_fallback') {
  if (typeof window === 'undefined') {
    return false
  }

  const response = await sendMessageToExtension({
    action: 'wakeRemoteQueuePolling',
    reason,
  })
  return Boolean(response?.success)
}

export async function performLogout(
  storage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | null = typeof localStorage === 'undefined'
    ? null
    : localStorage,
  apiBaseUrl?: string
) {
  if (!storage?.getItem) {
    return
  }
  const token = resolveAuthToken(storage)
  if (token && typeof fetch !== 'undefined') {
    try {
      const baseUrl = resolveApiBaseUrl(apiBaseUrl)
      await fetch(`${baseUrl}/api/v1/auth/logout`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
    } catch (error) {
      // ignore logout errors
    }
  }

  await clearBrowserLocalCacheForAuthSession(storage, apiBaseUrl)
  clearAuthSession(storage, { clearBrowserLocalCache: false })
}

export async function clearBrowserLocalCacheForAuthSession(
  storage: Pick<Storage, 'getItem'> | null = typeof localStorage === 'undefined' ? null : localStorage,
  apiBaseUrl?: string
) {
  if (!storage?.getItem) {
    return {
      userScope: '',
      deletedCount: 0,
      storeCounts: {},
    }
  }
  const token = resolveAuthToken(storage)
  const user = resolveAuthUser(storage)
  if (!token && !user?.id) {
    return {
      userScope: '',
      deletedCount: 0,
      storeCounts: {},
    }
  }
  const namespace = createBrowserLocalCacheNamespace({
    apiBaseUrl: resolveApiBaseUrl(apiBaseUrl),
    environment: typeof window === 'undefined' ? undefined : window.location.hostname,
    userId: user?.id || null,
    authScope: token,
  })
  try {
    return await clearBrowserLocalCacheUserScope(createIndexedDbLocalCacheDriver(), namespace.userScope)
  } catch {
    return {
      userScope: namespace.userScope,
      deletedCount: 0,
      storeCounts: {},
    }
  }
}

const AUTH_SESSION_STORAGE_KEYS = [
  'token',
  'access_token',
  'auth_token',
  'authToken',
  'ozon_token',
  'guest_token',
  'guestToken',
  'auth',
  'guest_user',
] as const

export function clearAuthSession(
  storage: Pick<Storage, 'getItem' | 'removeItem'> | null = typeof localStorage === 'undefined' ? null : localStorage,
  options: { clearBrowserLocalCache?: boolean } = {}
) {
  if (!storage?.removeItem) {
    return
  }
  if (options.clearBrowserLocalCache !== false) {
    void clearBrowserLocalCacheForAuthSession(storage).catch(() => undefined)
  }
  for (const key of AUTH_SESSION_STORAGE_KEYS) {
    try {
      storage.removeItem(key)
    } catch (error) {
      // ignore storage errors
    }
  }
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event('auth:updated'))
  }
}

export async function saveAuthSession(
  payload: AuthPayload | null,
  storage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | null = typeof localStorage === 'undefined'
    ? null
    : localStorage,
  apiBaseUrl?: string
) {
  if (!payload?.token || !storage?.setItem) {
    return false
  }

  const previousToken = resolveAuthToken(storage)
  const previousUserId = String(resolveAuthUser(storage)?.id ?? '').trim()
  const nextUserId = String(payload.user?.id ?? '').trim()
  const isAccountSwitch = Boolean(previousToken || previousUserId) && (
    String(previousToken || '') !== payload.token ||
    previousUserId !== nextUserId
  )

  if (isAccountSwitch) {
    await clearBrowserLocalCacheForAuthSession(storage, apiBaseUrl)
  }

  try {
    storage.setItem('token', payload.token)
    storage.setItem('auth', JSON.stringify(payload ?? {}))
    storage.removeItem('guest_token')
    storage.removeItem('guest_user')
  } catch {
    return false
  }

  return true
}

const ADMIN_TOKEN_KEY = 'admin_token'

export function resolveAdminToken(
  storage: Pick<Storage, 'getItem'> | null = typeof localStorage === 'undefined' ? null : localStorage
) {
  if (!storage?.getItem) {
    return null
  }
  return storage.getItem(ADMIN_TOKEN_KEY)
}

export function saveAdminToken(
  token: string,
  storage: Pick<Storage, 'setItem'> | null = typeof localStorage === 'undefined' ? null : localStorage
) {
  if (!storage?.setItem) {
    return
  }
  try {
    storage.setItem(ADMIN_TOKEN_KEY, token)
  } catch (error) {
    // ignore storage errors
  }
}

export function clearAdminToken(
  storage: Pick<Storage, 'removeItem'> | null = typeof localStorage === 'undefined' ? null : localStorage
) {
  if (!storage?.removeItem) {
    return
  }
  try {
    storage.removeItem(ADMIN_TOKEN_KEY)
  } catch (error) {
    // ignore storage errors
  }
}

export type AuthUserPayload = {
  id?: string
  phone?: string
  email?: string
  avatar?: string | null
  plan?: string
  plan_name?: string
  status?: string
  is_admin?: boolean
  nickname?: string
  username?: string
}

export type AuthPayload = {
  token?: string
  user?: AuthUserPayload
  quota?: Record<string, unknown>
}

export function resolveAuthPayload(
  storage: Pick<Storage, 'getItem'> | null = typeof localStorage === 'undefined' ? null : localStorage
) {
  if (!storage?.getItem) {
    return null
  }
  const raw = storage.getItem('auth')
  if (!raw) {
    return null
  }
  try {
    return JSON.parse(raw) as AuthPayload
  } catch (error) {
    return null
  }
}

export function resolveAuthUser(
  storage: Pick<Storage, 'getItem'> | null = typeof localStorage === 'undefined' ? null : localStorage
) {
  const payload = resolveAuthPayload(storage)
  if (payload?.user) {
    return payload.user
  }
  if (!storage?.getItem) {
    return null
  }
  const guestRaw = storage.getItem('guest_user')
  if (!guestRaw) {
    return null
  }
  try {
    const parsed = JSON.parse(guestRaw)
    if (parsed && typeof parsed === 'object') {
      return parsed as AuthUserPayload
    }
  } catch (error) {
    return null
  }
  return null
}

const AUTH_PROFILE_REFRESH_TTL_MS = 30_000
let authProfileRefreshInflight: Promise<AuthPayload | null> | null = null
let authProfileRefreshCache:
  | {
      token: string
      at: number
      payload: AuthPayload | null
    }
  | null = null

export async function refreshAuthProfile(
  storage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | null = typeof localStorage === 'undefined'
    ? null
    : localStorage,
  apiBaseUrl?: string
) {
  if (!storage?.getItem || typeof fetch === 'undefined') {
    return null
  }
  const token = resolveAuthToken(storage)
  if (!token) {
    return null
  }
  const now = Date.now()
  if (
    authProfileRefreshCache &&
    authProfileRefreshCache.token === token &&
    now - authProfileRefreshCache.at <= AUTH_PROFILE_REFRESH_TTL_MS
  ) {
    return authProfileRefreshCache.payload
  }
  if (authProfileRefreshInflight) {
    return await authProfileRefreshInflight
  }

  const baseUrl = resolveApiBaseUrl(apiBaseUrl)
  authProfileRefreshInflight = (async () => {
    try {
      const response = await fetch(`${baseUrl}/api/v1/auth/me`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      })
      if (!response.ok) {
        authProfileRefreshCache = { token, at: Date.now(), payload: null }
        return null
      }
      const payload = await response.json().catch(() => null)
      if (!payload?.user) {
        authProfileRefreshCache = { token, at: Date.now(), payload: null }
        return null
      }
      const existing = resolveAuthPayload(storage) ?? {}
      const nextPayload = {
        ...existing,
        ...payload,
        token: existing?.token || token,
        user: payload.user,
        quota: payload.quota ?? existing?.quota
      }
      storage.setItem('auth', JSON.stringify(nextPayload))
      authProfileRefreshCache = { token, at: Date.now(), payload: nextPayload }
      return nextPayload
    } catch (error) {
      authProfileRefreshCache = { token, at: Date.now(), payload: null }
      return null
    } finally {
      authProfileRefreshInflight = null
    }
  })()
  return await authProfileRefreshInflight
}

export const __resetAuthProfileRefreshCacheForTests = () => {
  authProfileRefreshInflight = null
  authProfileRefreshCache = null
}

export function buildAuthModalPath(
  pathname: string,
  search: string,
  mode: 'login' | 'signup' | 'forgot' | 'reset'
) {
  const params = new URLSearchParams(search)
  params.set('auth', mode)
  const nextSearch = params.toString()
  return `${pathname}${nextSearch ? `?${nextSearch}` : ''}`
}

export type UserStage = {
  stage: 'guest' | 'new' | 'active'
  isAuthenticated: boolean
  plan: string
  isAdmin: boolean
}

export function resolveUserStage(
  storage: Pick<Storage, 'getItem'> | null = typeof localStorage === 'undefined' ? null : localStorage
): UserStage {
  const token = resolveAuthToken(storage)
  if (!token) {
    return {
      stage: 'guest',
      isAuthenticated: false,
      plan: 'free',
      isAdmin: false
    }
  }

  const user = resolveAuthUser(storage)
  const plan = user?.plan || 'free'
  const isAdmin = Boolean(user?.is_admin)
  const stage = plan && plan !== 'free' ? 'active' : 'new'
  return {
    stage,
    isAuthenticated: true,
    plan,
    isAdmin
  }
}

export const IMAGE_CREDITS_PER_IMAGE = 10
export const CREDITS_PER_DOLLAR = 100

export type CreditPackId = 'basic' | 'growth' | 'scale'

export type CreditPackPreset = {
  id: CreditPackId
  credits: number
  priceUsd: number
  bonusPercent: number
  highlight?: boolean
}

export const CREDIT_PACK_PRESETS: CreditPackPreset[] = [
  { id: 'basic', credits: 1000, priceUsd: 10, bonusPercent: 0 },
  { id: 'growth', credits: 3000, priceUsd: 27, bonusPercent: 10, highlight: true },
  { id: 'scale', credits: 8000, priceUsd: 68, bonusPercent: 15 }
]

export type SubscriptionPlanId = 'starter' | 'pro' | 'business'

export type SubscriptionPlanPreset = {
  id: SubscriptionPlanId
  monthlyUsd: number
  yearlyUsd: number
  creditsMonthly: number | null
  highlight?: boolean
}

export const SUBSCRIPTION_PLAN_PRESETS: SubscriptionPlanPreset[] = [
  { id: 'starter', monthlyUsd: 19, yearlyUsd: 190, creditsMonthly: 2000 },
  { id: 'pro', monthlyUsd: 49, yearlyUsd: 490, creditsMonthly: 5600, highlight: true },
  { id: 'business', monthlyUsd: 99, yearlyUsd: 990, creditsMonthly: 12000 }
]

export function creditsToImages(credits?: number | null) {
  if (!credits) {
    return 0
  }
  return Math.floor(credits / IMAGE_CREDITS_PER_IMAGE)
}

export const formatAdminQuantity = (value: number | string | null | undefined, unit = '') => {
  const parsed = Number(value)
  if (!Number.isFinite(parsed)) {
    return unit ? `0 ${unit}` : '0'
  }
  const text = new Intl.NumberFormat(undefined, {
    maximumFractionDigits: 0,
    minimumFractionDigits: 0
  }).format(parsed)
  return unit ? `${text} ${unit}` : text
}

export const formatAdminMoney = (value: number | string | null | undefined, currency = '') => {
  const parsed = Number(value)
  if (!Number.isFinite(parsed)) {
    return currency ? `${currency} 0.00` : '0.00'
  }

  const normalized = String(currency || '').trim()
  if (!normalized) {
    return new Intl.NumberFormat(undefined, {
      maximumFractionDigits: 2,
      minimumFractionDigits: 2
    }).format(parsed)
  }

  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: normalized.toUpperCase(),
      maximumFractionDigits: 2,
      minimumFractionDigits: 2
    }).format(parsed)
  } catch (_error) {
    const text = new Intl.NumberFormat(undefined, {
      maximumFractionDigits: 2,
      minimumFractionDigits: 2
    }).format(Math.abs(parsed))
    const sign = parsed < 0 ? '-' : ''
    return `${sign}${normalized} ${text}`
  }
}

const ADMIN_BEIJING_TIMEZONE = 'Asia/Shanghai'
const ADMIN_DATE_INPUT_PATTERN = /^\d{4}-\d{2}-\d{2}$/

const parseAdminDateInputParts = (value: string): { year: number; month: number; day: number } | null => {
  const match = value.match(ADMIN_DATE_INPUT_PATTERN)
  if (!match) {
    return null
  }

  const year = Number.parseInt(value.slice(0, 4), 10)
  const month = Number.parseInt(value.slice(5, 7), 10)
  const day = Number.parseInt(value.slice(8, 10), 10)
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) {
    return null
  }
  if (month < 1 || month > 12) {
    return null
  }
  if (day < 1 || day > 31) {
    return null
  }

  const testDate = new Date(Date.UTC(year, month - 1, day))
  if (
    testDate.getUTCFullYear() !== year
    || testDate.getUTCMonth() !== month - 1
    || testDate.getUTCDate() !== day
  ) {
    return null
  }

  return { year, month, day }
}

const normalizeAdminDateInputParts = (value: Date): string => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: ADMIN_BEIJING_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(value)

  const year = parts.find((part) => part.type === 'year')?.value ?? ''
  const month = parts.find((part) => part.type === 'month')?.value ?? ''
  const day = parts.find((part) => part.type === 'day')?.value ?? ''

  if (!year || !month || !day) {
    return ''
  }

  return `${year}-${month}-${day}`
}

export const getBeijingDateInputValue = (value: Date = new Date()): string => {
  return normalizeAdminDateInputParts(value)
}

export const normalizeAdminDateInput = (value: string | null | undefined): string => {
  const normalized = String(value ?? '').trim()
  if (!normalized) {
    return ''
  }
  return parseAdminDateInputParts(normalized) ? normalized : ''
}

export const toBeijingNaturalDayBoundaryIso = (dateValue: string, boundary: 'start' | 'end'): string | null => {
  const normalized = normalizeAdminDateInput(dateValue)
  if (!normalized) {
    return null
  }

  const parsed = parseAdminDateInputParts(normalized)
  if (!parsed) {
    return null
  }

  const timestamp = boundary === 'start'
    ? Date.UTC(parsed.year, parsed.month - 1, parsed.day, -8, 0, 0, 0)
    : Date.UTC(parsed.year, parsed.month - 1, parsed.day, 15, 59, 59, 999)
  return new Date(timestamp).toISOString()
}

export const normalizeAdminNaturalDayRange = (
  startDateValue: string | null | undefined,
  endDateValue: string | null | undefined,
): { startDate: string; endDate: string } => {
  let startDate = normalizeAdminDateInput(startDateValue)
  let endDate = normalizeAdminDateInput(endDateValue)

  if (startDate && endDate && endDate < startDate) {
    ;[startDate, endDate] = [endDate, startDate]
  }

  return { startDate, endDate }
}

export const getDefaultAdminNaturalDayRange = (daySpan = 1): { startDate: string; endDate: string } => {
  const normalizedDaySpan = Number.isFinite(daySpan) ? Math.max(0, Math.floor(daySpan)) : 1
  if (normalizedDaySpan === 0) {
    return {
      startDate: '',
      endDate: '',
    }
  }

  const endDate = getBeijingDateInputValue()
  if (normalizedDaySpan === 1) {
    return {
      startDate: endDate,
      endDate,
    }
  }

  const endBoundaryIso = toBeijingNaturalDayBoundaryIso(endDate, 'start')
  const endBoundaryDate = endBoundaryIso ? new Date(endBoundaryIso) : new Date()
  const startDate = getBeijingDateInputValue(
    new Date(endBoundaryDate.getTime() - (normalizedDaySpan - 1) * 24 * 60 * 60 * 1000)
  )

  return {
    startDate,
    endDate,
  }
}

export const buildAdminNaturalDayQueryRange = (
  startDateValue: string | null | undefined,
  endDateValue: string | null | undefined,
): { startDate: string; endDate: string; startAt: string | null; endAt: string | null } => {
  const { startDate, endDate } = normalizeAdminNaturalDayRange(startDateValue, endDateValue)
  return {
    startDate,
    endDate,
    startAt: startDate ? toBeijingNaturalDayBoundaryIso(startDate, 'start') : null,
    endAt: endDate ? toBeijingNaturalDayBoundaryIso(endDate, 'end') : null,
  }
}

export function buildCreditsSummary(credits: number, creditsLabel = 'Credits', imagesLabel = 'Images') {
  const images = creditsToImages(credits)
  return `${credits} ${creditsLabel} · ${images} ${imagesLabel}`
}

export function isInsufficientCreditsMessage(value: unknown) {
  if (!value) {
    return false
  }
  const text = String(value)
  return /积分不足|余额不足|insufficient|not enough credits|quota_exhausted|recharge/i.test(text)
}

export function extractCreditsNumbers(message: string): { needed?: number; current?: number } | null {
  const text = String(message || '')
  if (!text) {
    return null
  }

  const patterns: Array<RegExp> = [
    /需要\s*(\d+)\s*积分[\s\S]*?(?:当前|现有|余额)\s*(\d+)\s*积分/i,
    /(?:当前|现有|余额)\s*[:：]?\s*(\d+)\s*积分[\s\S]*?需要\s*[:：]?\s*(\d+)\s*积分/i,
    /当前\s*(\d+)\s*积分\s*,?\s*需要\s*(\d+)\s*积分/i,
  ]

  for (const pattern of patterns) {
    const match = text.match(pattern)
    if (!match) {
      continue
    }
    if (pattern === patterns[1] || pattern === patterns[2]) {
      const current = Number(match[1])
      const needed = Number(match[2])
      return {
        current: Number.isFinite(current) ? current : undefined,
        needed: Number.isFinite(needed) ? needed : undefined,
      }
    }
    const needed = Number(match[1])
    const current = Number(match[2])
    return {
      needed: Number.isFinite(needed) ? needed : undefined,
      current: Number.isFinite(current) ? current : undefined,
    }
  }

  return null
}

export type AgentStatus = 'IDLE' | 'THINKING' | 'ACTIVE' | 'COMPLETE' | 'ERROR'

export function resolveStudioCollectionId(state: unknown, search: string) {
  if (state && typeof state === 'object' && 'collectionId' in state) {
    const value = (state as { collectionId?: unknown }).collectionId
    if (typeof value === 'string' && value) {
      return value
    }
  }

  const params = new URLSearchParams(search)
  return params.get('collectionId') || params.get('collection_id') || ''
}

export function mapStageStatusToAgentStatus(status?: string): AgentStatus {
  if (!status) {
    return 'IDLE'
  }

  if (status === 'completed' || status === 'skipped') {
    return 'COMPLETE'
  }

  if (status === 'failed') {
    return 'ERROR'
  }

  if (status === 'running') {
    return 'ACTIVE'
  }

  return 'IDLE'
}

export type StageStatusMap = Record<string, {
  stage_run_id?: string | null
  run_id?: string | null
  attempt?: number | null
  stage?: string
  status?: string
  runtime_stage_status?: string
  progress_percent?: number
  execution_time_ms?: number
  error?: string
  error_code?: string | null
  updated_at?: string
  runtime_mode?: 'foreground' | 'background' | string
  running_seconds?: number
  soft_timeout_at?: string | null
  hard_timeout_at?: string | null
  from_cache?: boolean
  dependencies?: string[]
  started_at?: string | null
  completed_at?: string | null
  failed_at?: string | null
}>

export function normalizeStudioStageKey(stageKey?: string | null): string {
  const normalized = String(stageKey ?? '').trim()
  if (!normalized) {
    return ''
  }
  if (normalized === 'visual') {
    return 'images'
  }
  return normalized
}

export function toStageStatusMap(response?: { stages?: StageStatusMap | null }) {
  const stages = response?.stages
  if (!stages || typeof stages !== 'object') {
    return {}
  }

  const normalized: StageStatusMap = {}
  Object.entries(stages).forEach(([rawStageKey, rawStageStatus]) => {
    if (rawStageKey === 'visual') {
      return
    }
    const stageKey = normalizeStudioStageKey(rawStageKey)
    if (!stageKey) {
      return
    }
    normalized[stageKey] = rawStageStatus ?? {}
  })

  if (!normalized.images && stages.visual && typeof stages.visual === 'object') {
    normalized.images = stages.visual
  }

  return normalized
}

export const DETAIL_SECTION_KEYS = ['intro_advantages', 'usage_evidence', 'specs_guarantee'] as const
export type DetailSectionKey = (typeof DETAIL_SECTION_KEYS)[number]

export type ImageSlot = {
  index?: number
  prompt?: string
  overlay_text?: string
  constraint_suffix?: string
  plan_group?: 'carousel' | 'detail'
  carousel_image_order?: number
  carousel_image_goal?: string
  detail_section_key?: DetailSectionKey | string
  detail_section_order?: number
  detail_section_title?: string
  detail_section_goal?: string
  generated_url?: string
  generated_raw_url?: string
  generated_thumbnail_url?: string
  generated_at?: string
  generated_version?: string | number
  candidate?: {
    status?: string
    raw_url?: string
    url?: string
    thumbnail_url?: string
    generated_at?: string
    version?: string | number
  } | null
  has_pending_candidate?: boolean
  history?: Array<{
    status?: string
    url?: string
    raw_url?: string
    thumbnail_url?: string
    recorded_at?: string
    version?: string | number
  }>
  image_type?: string
  aspect_ratio?: string
  keywords?: string[]
  scene?: string
}

export type ImageSlotsResponse = {
  image_slots?: ImageSlot[] | null
  carousel_slots?: ImageSlot[] | null
  detail_slots?: ImageSlot[] | null
  generation_status?: unknown
  regeneration_status?: unknown
  selected_detail_route?: CapabilityDetailGenerationMode
}

export function normalizeImageSlotGroup(value: unknown): 'carousel' | 'detail' {
  return value === 'detail' ? 'detail' : 'carousel'
}

export function normalizeImageSlots(payload?: ImageSlotsResponse) {
  const groupedPlans = [
    ...(Array.isArray(payload?.carousel_slots) ? payload.carousel_slots : []),
    ...(Array.isArray(payload?.detail_slots) ? payload.detail_slots : []),
  ]
  const plans = Array.isArray(payload?.image_slots) && payload.image_slots.length > 0 ? payload.image_slots : groupedPlans
  if (!Array.isArray(plans) || plans.length === 0) {
    return []
  }
  return plans
    .filter((plan): plan is ImageSlot => Boolean(plan && typeof plan === 'object'))
    .map((plan) => {
      const next: ImageSlot = { ...plan }
      next.plan_group = normalizeImageSlotGroup(next.plan_group)
      if (typeof next.carousel_image_goal === 'string') {
        next.carousel_image_goal = next.carousel_image_goal.trim()
      }
      if (!next.carousel_image_goal) {
        delete next.carousel_image_goal
      }
      if (typeof next.carousel_image_order === 'number') {
        if (!Number.isFinite(next.carousel_image_order) || next.carousel_image_order <= 0) {
          delete next.carousel_image_order
        }
      } else {
        delete next.carousel_image_order
      }
      if (typeof next.detail_section_key === 'string') {
        next.detail_section_key = next.detail_section_key.trim()
      }
      if (!next.detail_section_key) {
        delete next.detail_section_key
      }
      if (typeof next.detail_section_title === 'string') {
        next.detail_section_title = next.detail_section_title.trim()
      }
      if (!next.detail_section_title) {
        delete next.detail_section_title
      }
      if (typeof next.detail_section_goal === 'string') {
        next.detail_section_goal = next.detail_section_goal.trim()
      }
      if (!next.detail_section_goal) {
        delete next.detail_section_goal
      }
      if (typeof next.detail_section_order === 'number') {
        if (!Number.isFinite(next.detail_section_order) || next.detail_section_order <= 0) {
          delete next.detail_section_order
        }
      } else {
        delete next.detail_section_order
      }
      if (typeof next.prompt === 'string') {
        next.prompt = next.prompt
          .replace(/(?:,\s*or\s*)?promotional\s+text\.?/gi, '')
          .replace(/\s+\./g, '.')
          .replace(/,\s*\./g, '.')
          .replace(/\.\s*\./g, '.')
          .replace(/\s{2,}/g, ' ')
          .trim()
      }
      if (typeof next.constraint_suffix === 'string') {
        next.constraint_suffix = next.constraint_suffix
          .replace(/(?:,\s*or\s*)?promotional\s+text\.?/gi, '')
          .replace(/\s+\./g, '.')
          .replace(/,\s*\./g, '.')
          .replace(/\.\s*\./g, '.')
          .replace(/\s{2,}/g, ' ')
          .trim()
      }
      return next
    })
}

export function buildGeneratePayload(input: {
  skipImages?: boolean
  useCache?: boolean
  fromStage?: string
  capability?: CapabilityProfile
  storeId?: number | null
  selectedSkuKey?: string | null
  selectedSkuAnchorImageIndex?: number | null
  supportingReferenceImageIndices?: unknown
}) {
  const supportingReferenceImageIndices = Array.isArray(input.supportingReferenceImageIndices)
    ? Array.from(new Set(input.supportingReferenceImageIndices
      .map((item) => Number(item))
      .filter((item) => Number.isInteger(item) && item >= 0)
      .map((item) => Math.trunc(item))))
      .slice(0, 4)
    : []

  return {
    skip_images: input.skipImages ?? false,
    use_cache: input.useCache ?? false,
    from_stage: input.fromStage ?? null,
    store_id: input.storeId ?? null,
    capability: input.capability ?? loadCapabilityProfile(),
    selected_sku_key: input.selectedSkuKey ?? null,
    selected_sku_anchor_image_index: input.selectedSkuAnchorImageIndex ?? null,
    supporting_reference_image_indices: supportingReferenceImageIndices,
  }
}

export type CapabilityStrategyTier = 'economy' | 'balanced' | 'premium'
export type CapabilityDetailGenerationMode = 'compose_from_carousel' | 'standalone_generate'

export interface CapabilityProfile {
  platform: 'ozon' | 'amazon' | 'shopify' | 'aliexpress' | 'ebay'
  market_region:
  | 'ru'
  | 'us'
  | 'uk'
  | 'de'
  | 'fr'
  | 'it'
  | 'es'
  | 'jp'
  | 'br'
  | 'ca'
  | 'mx'
  | 'au'
  | 'sg'
  | 'in'
  | 'tr'
  | 'ae'
  | 'sa'
  | 'nl'
  | 'se'
  | 'pl'
  | 'be'
  | 'ie'
  | 'za'
  | 'eg'
  | 'global'
  strategy_tier: CapabilityStrategyTier
  image_max_images?: number | null
  detail_images_enabled?: boolean
  detail_generation_mode?: CapabilityDetailGenerationMode
  brand_guard: boolean
  language:
  | 'ru'
  | 'en'
  | 'de'
  | 'fr'
  | 'it'
  | 'es'
  | 'ja'
  | 'pt-br'
  | 'pt-pt'
  | 'nl'
  | 'sv'
  | 'pl'
  | 'tr'
  | 'ar'
  | 'zh-hans'
  | 'zh-hant'
  | 'cs'
  | 'da'
  | 'fi'
  | 'ko'
  | 'nb'
  | 'th'
  content_style: 'professional' | 'creative' | 'concise' | 'custom'
  custom_prompt: string
  seo_level: 'standard' | 'deep'
}

export interface CapabilityContext extends CapabilityProfile {
  image_generation: {
    enabled: boolean
    max_images: number
    detail_images_enabled: boolean
    detail_image_count: number
    detail_generation_mode: CapabilityDetailGenerationMode
    style_preset: string
  }
  rich_content: {
    enabled: boolean
    theme: string
    generation_mode: CapabilityDetailGenerationMode
  }
  model_preset: {
    text: string
    image: string
  }
  summary: string[]
}

const CAPABILITY_STORAGE_KEY = 'capability_profile'
const CAPABILITY_SETTINGS_SKIP_STORAGE_KEY = 'capability_settings_skip'
export const IMAGE_COUNT_SELECTION_OPTIONS = [
  { key: 'bundle-5', count: 5 },
  { key: 'bundle-7', count: 7 },
  { key: 'bundle-10', count: 10 },
] as const
export const STANDARD_IMAGE_COUNT_OPTIONS = [5, 7, 10] as const
export const DETAIL_IMAGE_COUNT_OPTION = 3
export const DEFAULT_IMAGE_COUNT_OPTION = 7
export const DEFAULT_DETAIL_GENERATION_MODE: CapabilityDetailGenerationMode = 'compose_from_carousel'
export const SUPPORTED_DETAIL_GENERATION_MODES = ['compose_from_carousel', 'standalone_generate'] as const

export type CapabilityPreferences = {
  capability_settings_skip: boolean
}

export const defaultCapabilityProfile: CapabilityProfile = {
  platform: 'ozon',
  market_region: 'ru',
  strategy_tier: 'balanced',
  image_max_images: DEFAULT_IMAGE_COUNT_OPTION,
  detail_images_enabled: false,
  detail_generation_mode: DEFAULT_DETAIL_GENERATION_MODE,
  brand_guard: true,
  language: 'ru',
  content_style: 'professional',
  custom_prompt: '',
  seo_level: 'deep'
}

export function normalizeDetailGenerationMode(
  value: unknown,
  fallback: CapabilityDetailGenerationMode = DEFAULT_DETAIL_GENERATION_MODE
): CapabilityDetailGenerationMode {
  if (value === 'compose_from_carousel' || value === 'standalone_generate') {
    return value
  }
  return fallback
}

type UploadPreviewPrimaryActionState = {
  isSubmittingPrimary: boolean
  isSavingDraft: boolean
  hasInvalidVariantContext: boolean
  isVariantPreviewMode: boolean
  hasPreview: boolean
  hasVariantPreviewItem: boolean
  hasLoadError: boolean
  isLoading: boolean
  action: string
  hasPendingImageOps: boolean
  isUpdateMode: boolean
  isUploaded: boolean
  hasOzonOfferId: boolean
}

export function shouldEnableUploadPreviewPrimaryAction(state: UploadPreviewPrimaryActionState): boolean {
  if (state.isSubmittingPrimary || state.isSavingDraft) {
    return false
  }
  if (state.hasInvalidVariantContext) {
    return false
  }
  if (state.isVariantPreviewMode) {
    return state.hasPreview && state.hasVariantPreviewItem && !state.hasLoadError && !state.isLoading
  }
  if (state.action === 'review') {
    return true
  }
  const baseReady = state.hasPreview && !state.hasLoadError && !state.isLoading && !state.hasPendingImageOps
  if (!baseReady) {
    return false
  }
  if (state.isUpdateMode) {
    return state.isUploaded && state.hasOzonOfferId
  }
  return true
}

export function normalizeSupportedImageCount(
  value: unknown,
  fallback: number = DEFAULT_IMAGE_COUNT_OPTION
): number {
  const resolved =
    typeof value === 'number' && Number.isFinite(value)
      ? Math.round(value)
      : typeof value === 'string' && value.trim()
        ? parseInt(value, 10)
        : fallback
  if (STANDARD_IMAGE_COUNT_OPTIONS.includes(resolved as (typeof STANDARD_IMAGE_COUNT_OPTIONS)[number])) {
    return resolved
  }
  const initialBest = STANDARD_IMAGE_COUNT_OPTIONS.includes(fallback as (typeof STANDARD_IMAGE_COUNT_OPTIONS)[number])
    ? fallback
    : STANDARD_IMAGE_COUNT_OPTIONS[0]
  return STANDARD_IMAGE_COUNT_OPTIONS.reduce((best, current) => {
    const bestDistance = Math.abs(best - resolved)
    const currentDistance = Math.abs(current - resolved)
    if (currentDistance < bestDistance) {
      return current
    }
    if (currentDistance === bestDistance && current < best) {
      return current
    }
    return best
  }, initialBest)
}

export function floorSupportedImageCount(value: unknown): number {
  const resolved =
    typeof value === 'number' && Number.isFinite(value)
      ? Math.floor(value)
      : typeof value === 'string' && value.trim()
        ? parseInt(value, 10)
        : 0
  const candidates = STANDARD_IMAGE_COUNT_OPTIONS.filter((item) => item <= resolved)
  return candidates.length ? candidates[candidates.length - 1] : 0
}

function normalizeCapabilityDetailImagesEnabled(value: unknown, fallback = true): boolean {
  if (typeof value === 'boolean') {
    return value
  }
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase()
    if (['1', 'true', 'yes', 'y', 'on'].includes(normalized)) {
      return true
    }
    if (['0', 'false', 'no', 'n', 'off'].includes(normalized)) {
      return false
    }
  }
  return fallback
}

export function loadCapabilityProfile(
  storage: Pick<Storage, 'getItem'> | null = typeof localStorage === 'undefined' ? null : localStorage
): CapabilityProfile {
  if (!storage?.getItem) {
    return { ...defaultCapabilityProfile }
  }
  const raw = storage.getItem(CAPABILITY_STORAGE_KEY)
  if (!raw) {
    return { ...defaultCapabilityProfile }
  }
  try {
    const parsed = JSON.parse(raw) as (Partial<CapabilityProfile> & { image_set_mode?: unknown }) | null
    if (!parsed) {
      return { ...defaultCapabilityProfile }
    }
    const legacyImageSetMode = parsed.image_set_mode
    const defaultDetailEnabled = true
    const defaultDetailMode = legacyImageSetMode === 'detail_only' ? 'standalone_generate' : DEFAULT_DETAIL_GENERATION_MODE
    return {
      ...defaultCapabilityProfile,
      ...parsed,
      image_max_images: normalizeSupportedImageCount(
        parsed.image_max_images,
        defaultCapabilityProfile.image_max_images ?? DEFAULT_IMAGE_COUNT_OPTION
      ),
      detail_images_enabled: normalizeCapabilityDetailImagesEnabled(
        parsed.detail_images_enabled,
        defaultDetailEnabled
      ),
      detail_generation_mode: normalizeDetailGenerationMode(
        parsed.detail_generation_mode,
        defaultDetailMode
      ),
      seo_level: 'deep'
    }
  } catch (error) {
    return { ...defaultCapabilityProfile }
  }
}

export function saveCapabilityProfile(
  profile: CapabilityProfile,
  storage: Pick<Storage, 'setItem'> | null = typeof localStorage === 'undefined' ? null : localStorage
) {
  if (!storage?.setItem) {
    return
  }
  try {
    storage.setItem(CAPABILITY_STORAGE_KEY, JSON.stringify({ ...profile, seo_level: 'deep' }))
  } catch (error) {
    // ignore storage errors
  }
}

export function loadCapabilitySettingsSkip(
  storage: Pick<Storage, 'getItem'> | null = typeof localStorage === 'undefined' ? null : localStorage
): boolean {
  if (!storage?.getItem) {
    return false
  }
  try {
    return storage.getItem(CAPABILITY_SETTINGS_SKIP_STORAGE_KEY) === '1'
  } catch (error) {
    return false
  }
}

export function persistCapabilitySettingsSkip(
  value: boolean,
  storage: Pick<Storage, 'setItem'> | null = typeof localStorage === 'undefined' ? null : localStorage
) {
  if (!storage?.setItem) {
    return
  }
  try {
    storage.setItem(CAPABILITY_SETTINGS_SKIP_STORAGE_KEY, value ? '1' : '0')
  } catch (error) {
    // ignore storage errors
  }
}

const appShellLocalCacheInflight = new Map<string, Promise<unknown>>()
const appShellLocalCacheRevalidateInflight = new Map<string, Promise<void>>()
const APP_SHELL_CACHE_HIT_REVALIDATE_DELAY_MS = 1500

const scheduleAppShellLocalCacheRevalidate = (
  inflightKey: string,
  revalidate: () => Promise<void>
): void => {
  if (appShellLocalCacheRevalidateInflight.has(inflightKey)) {
    return
  }

  const revalidateTask = new Promise<void>((resolve) => {
    const run = () => {
      void revalidate()
        .catch(() => undefined)
        .finally(resolve)
    }
    if (typeof window !== 'undefined' && typeof window.setTimeout === 'function') {
      window.setTimeout(run, APP_SHELL_CACHE_HIT_REVALIDATE_DELAY_MS)
      return
    }
    setTimeout(run, APP_SHELL_CACHE_HIT_REVALIDATE_DELAY_MS)
  })
  appShellLocalCacheRevalidateInflight.set(inflightKey, revalidateTask)
  void revalidateTask.finally(() => {
    if (appShellLocalCacheRevalidateInflight.get(inflightKey) === revalidateTask) {
      appShellLocalCacheRevalidateInflight.delete(inflightKey)
    }
  })
}

const createRuntimeAppShellLocalCacheContext = (): {
  apiBaseUrl: string
  context: AppShellLocalCacheContext
  token: string | null
  authHeaders: Record<string, string>
} | null => {
  const token = resolveAuthToken()
  if (!token) {
    return null
  }
  const apiBaseUrl = resolveApiBaseUrl()
  const user = resolveAuthUser()
  const context = createAppShellLocalCacheContext({
    apiBaseUrl,
    environment: typeof window === 'undefined' ? undefined : window.location.hostname,
    userId: user?.id || null,
    authScope: token,
    cacheSeed: token,
  })
  if (!context) {
    return null
  }
  return {
    apiBaseUrl,
    context,
    token,
    authHeaders: { Authorization: `Bearer ${token}` },
  }
}

const createPublicAppShellLocalCacheContext = (input?: { apiBaseUrl?: string; publicScope?: string }): {
  apiBaseUrl: string
  context: AppShellLocalCacheContext
  token: null
  authHeaders: Record<string, string>
} | null => {
  const apiBaseUrl = resolveApiBaseUrl(input?.apiBaseUrl)
  const publicScope = String(input?.publicScope || 'capabilities-options').trim() || 'capabilities-options'
  const context = createAppShellLocalCacheContext({
    apiBaseUrl,
    environment: typeof window === 'undefined' ? undefined : window.location.hostname,
    userId: null,
    authScope: `public:${publicScope}`,
    cacheSeed: `public:${publicScope}:${apiBaseUrl}`,
  })
  if (!context) {
    return null
  }
  return {
    apiBaseUrl,
    context,
    token: null,
    authHeaders: {},
  }
}

const normalizeCapabilityPreferencesPayload = (value: unknown): CapabilityPreferences | null => {
  const prefs = value && typeof value === 'object' ? value as Record<string, unknown> : null
  if (!prefs) {
    return null
  }
  return {
    capability_settings_skip: Boolean(prefs.capability_settings_skip),
  }
}

const normalizeCapabilityProfilePayload = (value: unknown): CapabilityProfile | null => {
  const profile = value && typeof value === 'object'
    ? value as Partial<CapabilityProfile> & { image_set_mode?: unknown }
    : null
  if (!profile) {
    return null
  }
  const legacyImageSetMode = profile.image_set_mode
  const defaultDetailMode = legacyImageSetMode === 'detail_only' ? 'standalone_generate' : DEFAULT_DETAIL_GENERATION_MODE
  return {
    ...defaultCapabilityProfile,
    ...profile,
    image_max_images: normalizeSupportedImageCount(
      profile.image_max_images,
      defaultCapabilityProfile.image_max_images ?? DEFAULT_IMAGE_COUNT_OPTION
    ),
    detail_images_enabled: normalizeCapabilityDetailImagesEnabled(profile.detail_images_enabled, true),
    detail_generation_mode: normalizeDetailGenerationMode(profile.detail_generation_mode, defaultDetailMode),
    seo_level: 'deep',
  }
}

type AppShellResponseCacheMetadata = {
  etag?: string
  lastModified?: string
  serverRevision?: string | number
  projectionVersion?: string | number
}

const normalizeAppShellCacheMetaValue = (value: unknown): string | number | undefined => (
  typeof value === 'string' || typeof value === 'number' ? value : undefined
)

const normalizeOptionalHeader = (value: string | null): string | undefined => {
  const normalized = String(value || '').trim()
  return normalized || undefined
}

const extractAppShellResponseCacheMetadata = (
  payload: unknown,
  response?: Pick<Response, 'headers'>
): AppShellResponseCacheMetadata => {
  const record = payload && typeof payload === 'object' ? payload as Record<string, unknown> : {}
  const meta = record.meta && typeof record.meta === 'object' ? record.meta as Record<string, unknown> : {}
  const serverRevision = normalizeAppShellCacheMetaValue(meta.server_revision)
  const projectionVersion = normalizeAppShellCacheMetaValue(meta.projection_version)
  const responseEtag = response ? normalizeOptionalHeader(response.headers.get('etag')) : undefined
  const revisionEtag = serverRevision !== undefined ? `W/"${serverRevision}"` : undefined
  return {
    etag: responseEtag || revisionEtag,
    lastModified: response ? normalizeOptionalHeader(response.headers.get('last-modified')) : undefined,
    serverRevision,
    projectionVersion,
  }
}

const fetchAppShellJsonWithLocalCache = async <TPayload>(
  options: {
    resourceName: AppShellLocalCacheResource
    path: string
    ttlMs: number
    entityType: string
    apiBaseUrl?: string
    forceRefresh?: boolean
    sensitivity?: 'high' | 'medium' | 'low'
    publicResource?: boolean
    publicScope?: string
    selectPayload: (payload: unknown) => TPayload | null
  }
): Promise<TPayload | null> => {
  const runtime = options.publicResource
    ? createPublicAppShellLocalCacheContext({
      apiBaseUrl: options.apiBaseUrl,
      publicScope: options.publicScope,
    })
    : createRuntimeAppShellLocalCacheContext()
  if (!runtime) {
    if (!options.publicResource) {
      return null
    }
  }
  const apiBaseUrl = runtime?.apiBaseUrl ?? resolveApiBaseUrl(options.apiBaseUrl)
  const authHeaders = runtime?.authHeaders ?? {}
  const context = runtime?.context ?? null
  const inflightKey = [
    context?.namespace.environment ?? 'public',
    context?.namespace.apiBaseHash ?? apiBaseUrl,
    context?.namespace.userScope ?? 'no-cache-context',
    options.resourceName,
    options.path,
  ].join('|')

  const fetchAuthoritative = async (input?: { ifNoneMatch?: string }): Promise<TPayload | null> => {
    try {
      const requestHeaders: Record<string, string> = { ...authHeaders }
      if (input?.ifNoneMatch) {
        requestHeaders['If-None-Match'] = input.ifNoneMatch
      }
      const response = await fetch(`${apiBaseUrl}${options.path}`, {
        headers: requestHeaders,
        cache: 'no-store',
      })
      if (response.status === 304) {
        if (context) {
          await refreshAppShellLocalCacheMetadata(context, options.resourceName, {
            ttlMs: options.ttlMs,
            etag: normalizeOptionalHeader(response.headers.get('etag')) || input?.ifNoneMatch,
            lastModified: normalizeOptionalHeader(response.headers.get('last-modified')),
          })
        }
        return null
      }
      if (!response.ok) {
        return null
      }
      const payload = await response.json().catch(() => null)
      const selectedPayload = options.selectPayload(payload)
      if (!selectedPayload) {
        return null
      }
      if (context) {
        try {
          await writeAppShellLocalCache(context, options.resourceName, selectedPayload, {
            ttlMs: options.ttlMs,
            entityType: options.entityType,
            sensitivity: options.sensitivity ?? 'medium',
            ...extractAppShellResponseCacheMetadata(payload, response),
          })
        } catch {
          // Cache write failures degrade to network-only for this request.
        }
      }
      return selectedPayload
    } catch {
      return null
    }
  }

  if (context && !options.forceRefresh) {
    try {
      const cached = await readAppShellLocalCache<TPayload>(context, options.resourceName)
      if (cached.status === 'hit') {
        const cachedEtag = String(cached.record.etag || '').trim()
        if (cachedEtag && !appShellLocalCacheRevalidateInflight.has(inflightKey)) {
          scheduleAppShellLocalCacheRevalidate(inflightKey, async () => {
            await fetchAuthoritative({ ifNoneMatch: cachedEtag })
          })
        }
        return cached.payload
      }
    } catch {
      // Cache read failures must not block the authoritative request.
    }
  }

  const existing = appShellLocalCacheInflight.get(inflightKey) as Promise<TPayload | null> | undefined
  if (existing) {
    return await existing
  }
  const task = fetchAuthoritative()
  appShellLocalCacheInflight.set(inflightKey, task)
  try {
    return await task
  } finally {
    appShellLocalCacheInflight.delete(inflightKey)
  }
}

export async function fetchCapabilityOptionsWithLocalCache<TPayload = Record<string, unknown>>(
  options: { forceRefresh?: boolean } = {}
): Promise<TPayload | null> {
  return await fetchAppShellJsonWithLocalCache<TPayload>({
    resourceName: 'capabilities:options',
    path: '/api/v1/capabilities/options',
    ttlMs: APP_SHELL_CAPABILITY_OPTIONS_TTL_MS,
    entityType: 'capability-options',
    forceRefresh: options.forceRefresh,
    sensitivity: 'low',
    publicResource: true,
    selectPayload: (payload) => (payload && typeof payload === 'object' ? payload as TPayload : null),
  })
}

export async function fetchCapabilityPreferences(options: { forceRefresh?: boolean } = {}): Promise<CapabilityPreferences | null> {
  return await fetchAppShellJsonWithLocalCache<CapabilityPreferences>({
    resourceName: 'capabilities:preferences',
    path: '/api/v1/capabilities/preferences',
    ttlMs: APP_SHELL_CAPABILITY_PREFERENCES_TTL_MS,
    entityType: 'capability-preferences',
    forceRefresh: options.forceRefresh,
    selectPayload: (payload) => {
      const record = payload && typeof payload === 'object' ? payload as Record<string, unknown> : {}
      return normalizeCapabilityPreferencesPayload(record.preferences)
    },
  })
}

export async function fetchCapabilityProfileWithLocalCache(
  options: { forceRefresh?: boolean } = {}
): Promise<CapabilityProfile | null> {
  return await fetchAppShellJsonWithLocalCache<CapabilityProfile>({
    resourceName: 'capabilities:profile',
    path: '/api/v1/capabilities/profile',
    ttlMs: APP_SHELL_CAPABILITY_PROFILE_TTL_MS,
    entityType: 'capability-profile',
    forceRefresh: options.forceRefresh,
    selectPayload: (payload) => {
      const record = payload && typeof payload === 'object' ? payload as Record<string, unknown> : {}
      return normalizeCapabilityProfilePayload(record.profile)
    },
  })
}

export async function writeCapabilityProfileToAppShellCache(profile: CapabilityProfile): Promise<void> {
  const runtime = createRuntimeAppShellLocalCacheContext()
  if (!runtime) {
    return
  }
  const normalizedProfile = normalizeCapabilityProfilePayload(profile)
  if (!normalizedProfile) {
    return
  }
  await writeAppShellLocalCache(runtime.context, 'capabilities:profile', normalizedProfile, {
    ttlMs: APP_SHELL_CAPABILITY_PROFILE_TTL_MS,
    entityType: 'capability-profile',
  })
}

export async function fetchStoreBindStatusWithLocalCache<TPayload = Record<string, unknown>>(
  options: { forceRefresh?: boolean } = {}
): Promise<TPayload | null> {
  return await fetchAppShellJsonWithLocalCache<TPayload>({
    resourceName: 'stores:bind-status',
    path: '/api/v1/stores/bind/status',
    ttlMs: APP_SHELL_STORE_BIND_STATUS_TTL_MS,
    entityType: 'store-bind-status',
    forceRefresh: options.forceRefresh,
    sensitivity: 'high',
    selectPayload: (payload) => (payload && typeof payload === 'object' ? payload as TPayload : null),
  })
}

export async function invalidateStoreBindStatusLocalCache(): Promise<void> {
  const runtime = createRuntimeAppShellLocalCacheContext()
  if (!runtime) {
    return
  }
  await deleteAppShellLocalCache(runtime.context, 'stores:bind-status')
}

export async function updateCapabilityPreferences(
  updates: Partial<CapabilityPreferences>
): Promise<CapabilityPreferences | null> {
  const token = resolveAuthToken()
  if (!token) {
    return null
  }
  const apiBaseUrl = resolveApiBaseUrl()
  try {
    const response = await fetch(`${apiBaseUrl}/api/v1/capabilities/preferences`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(updates),
    })
    if (!response.ok) {
      return null
    }
    const payload = (await response.json().catch(() => null)) as any
    const prefs = payload?.preferences
    if (!prefs || typeof prefs !== 'object') {
      return null
    }
    const normalizedPreferences = normalizeCapabilityPreferencesPayload(prefs)
    const runtime = createRuntimeAppShellLocalCacheContext()
    if (normalizedPreferences && runtime) {
      await writeAppShellLocalCache(runtime.context, 'capabilities:preferences', normalizedPreferences, {
        ttlMs: APP_SHELL_CAPABILITY_PREFERENCES_TTL_MS,
        entityType: 'capability-preferences',
        ...extractAppShellResponseCacheMetadata(payload, response),
      })
    }
    return normalizedPreferences
  } catch (error) {
    return null
  }
}

export async function syncCapabilityPreferences(): Promise<CapabilityPreferences | null> {
  const prefs = await fetchCapabilityPreferences()
  if (!prefs) {
    return null
  }
  persistCapabilitySettingsSkip(prefs.capability_settings_skip)
  return prefs
}

export async function setCapabilitySettingsSkip(value: boolean): Promise<boolean> {
  persistCapabilitySettingsSkip(value)
  const prefs = await updateCapabilityPreferences({ capability_settings_skip: value })
  if (!prefs) {
    return value
  }
  persistCapabilitySettingsSkip(prefs.capability_settings_skip)
  return prefs.capability_settings_skip
}

export type AiConfigStyle = 'professional' | 'creative' | 'concise' | 'custom'
export type AiConfigSeoLevel = 'standard' | 'deep'
export type AiConfig = {
  platform: CapabilityProfile['platform']
  marketRegion: CapabilityProfile['market_region']
  contentStyle: AiConfigStyle
  seoLevel: AiConfigSeoLevel
  language: CapabilityProfile['language']
  enableImageGeneration: boolean
  imageCount: number
  detailImagesEnabled: boolean
  detailGenerationMode: CapabilityDetailGenerationMode
}

export function mapCapabilityProfileToAiConfig(profile: CapabilityProfile): AiConfig {
  const presets: Record<CapabilityStrategyTier, number> = {
    economy: 5,
    balanced: 7,
    premium: 10
  }
  const fallbackCount = presets[profile.strategy_tier] ?? presets.balanced
  return {
    platform: profile.platform,
    marketRegion: profile.market_region,
    contentStyle: profile.content_style,
    seoLevel: 'deep',
    language: profile.language,
    enableImageGeneration: true,
    imageCount: normalizeSupportedImageCount(
      (profile as { image_max_images?: unknown }).image_max_images,
      fallbackCount,
    ),
    detailImagesEnabled: normalizeCapabilityDetailImagesEnabled(profile.detail_images_enabled, false),
    detailGenerationMode: normalizeDetailGenerationMode(
      profile.detail_generation_mode,
      DEFAULT_DETAIL_GENERATION_MODE,
    )
  }
}

export function mapAiConfigToCapabilityProfile(
  config: AiConfig,
  baseProfile: CapabilityProfile
): CapabilityProfile {
  return {
    ...baseProfile,
    platform: config.platform,
    market_region: config.marketRegion,
    content_style: config.contentStyle,
    language: config.language,
    image_max_images: normalizeSupportedImageCount(config.imageCount, DEFAULT_IMAGE_COUNT_OPTION),
    detail_images_enabled: Boolean(config.detailImagesEnabled),
    detail_generation_mode: normalizeDetailGenerationMode(
      config.detailGenerationMode,
      DEFAULT_DETAIL_GENERATION_MODE,
    ),
    seo_level: 'deep'
  }
}

export const defaultAiConfig: AiConfig = mapCapabilityProfileToAiConfig(defaultCapabilityProfile)

const AI_CONFIG_STORAGE_KEY = 'ai_config'

export function loadAiConfig(
  storage: Pick<Storage, 'getItem'> | null = typeof localStorage === 'undefined' ? null : localStorage
): AiConfig {
  return mapCapabilityProfileToAiConfig(loadCapabilityProfile(storage))
}

export function saveAiConfig(
  config: AiConfig,
  storage: Pick<Storage, 'getItem' | 'setItem'> | null = typeof localStorage === 'undefined' ? null : localStorage
) {
  if (!storage?.setItem) {
    return
  }
  const baseProfile = loadCapabilityProfile(storage)
  const nextProfile = mapAiConfigToCapabilityProfile(config, baseProfile)
  saveCapabilityProfile(nextProfile, storage)
  try {
    storage.setItem(AI_CONFIG_STORAGE_KEY, JSON.stringify({ ...config, seoLevel: 'deep' }))
  } catch (error) {
    // ignore storage errors
  }
}

const normalizeApiBaseUrl = (rawBaseUrl: string, originOverride?: string) => {
  const trimmedBaseUrl = rawBaseUrl.trim().replace(/\/$/, '')
  if (!trimmedBaseUrl) {
    return trimmedBaseUrl
  }

  const browserOrigin = typeof window !== 'undefined' && window.location?.origin ? window.location.origin : ''
  const origin = originOverride ?? browserOrigin
  if (!origin) {
    return trimmedBaseUrl
  }

  try {
    const pageUrl = new URL(origin)
    const apiUrl = new URL(trimmedBaseUrl)
    const isSecurePage = pageUrl.protocol === 'https:'
    const isPlatformApiHost = apiUrl.hostname === 'api.yijianmai.com' || apiUrl.hostname === 'staging-api.yijianmai.com'
    if (isSecurePage && isPlatformApiHost && apiUrl.protocol === 'http:') {
      apiUrl.protocol = 'https:'
      return apiUrl.toString().replace(/\/$/, '')
    }
  } catch (_error) {
    return trimmedBaseUrl
  }

  return trimmedBaseUrl
}

export function resolveApiBaseUrl(envBaseUrl?: string, originOverride?: string) {
  const cleanedEnv = envBaseUrl?.trim()
  if (cleanedEnv) {
    return normalizeApiBaseUrl(cleanedEnv, originOverride)
  }

  const metaEnv = (import.meta as { env?: { VITE_API_BASE_URL?: string } }).env?.VITE_API_BASE_URL
  if (metaEnv) {
    return normalizeApiBaseUrl(metaEnv, originOverride)
  }

  const origin = originOverride ?? (typeof window !== 'undefined' ? window.location.origin : '')
  if (origin) {
    const trimmedOrigin = origin.replace(/\/$/, '')
    try {
      const url = new URL(trimmedOrigin)
      const hostname = url.hostname
      const port = url.port
      const normalizedHost = hostname === '0.0.0.0' ? 'localhost' : hostname
      if (normalizedHost === 'yijianmai.com' || normalizedHost === 'www.yijianmai.com') {
        return `${url.protocol}//api.yijianmai.com`
      }
      if (normalizedHost === 'staging.yijianmai.com') {
        return `${url.protocol}//staging-api.yijianmai.com`
      }
      const isLocalHost = normalizedHost === 'localhost' || normalizedHost === '127.0.0.1' || normalizedHost === '::1'
      const isPrivateIp =
        normalizedHost.startsWith('10.') ||
        normalizedHost.startsWith('192.168.') ||
        /^172\.(1[6-9]|2\d|3[0-1])\./.test(hostname)
      if ((isLocalHost || isPrivateIp) && port && port !== '8000') {
        return `${url.protocol}//${normalizedHost}:8000`
      }
    } catch (error) {
      if (trimmedOrigin.includes(':5666')) {
        if (trimmedOrigin.includes('0.0.0.0')) {
          return trimmedOrigin.replace('0.0.0.0:5666', 'localhost:8000')
        }
        return trimmedOrigin.replace(':5666', ':8000')
      }
    }
    return trimmedOrigin
  }

  return 'http://localhost:8000'
}

export type MarketingEventPayload = {
  event_name: string
  event_source?: string
  page_url?: string
  page_path?: string
  referrer_url?: string
  visitor_id?: string
  session_id?: string
  language?: string
  occurred_at?: string
  utm_source?: string
  utm_medium?: string
  utm_campaign?: string
  utm_term?: string
  utm_content?: string
  properties?: Record<string, unknown>
}

type SendMarketingEventOptions = {
  apiBaseUrl?: string
  transport?: 'fetch' | 'beacon'
  keepalive?: boolean
}

type PendingMarketingBatch = {
  events: MarketingEventPayload[]
  options: SendMarketingEventOptions[]
  resolvers: Array<(ok: boolean) => void>
}

const MARKETING_VISITOR_ID_STORAGE_KEY = 'marketing-visitor-id'
const MARKETING_SESSION_ID_STORAGE_KEY = 'marketing-session-id'
const MARKETING_PAGE_PATH_MAX_LENGTH = 255
const MARKETING_EVENT_BATCH_WINDOW_MS = 2000

let pendingMarketingBatch: PendingMarketingBatch | null = null
let pendingMarketingBatchTimer: ReturnType<typeof setTimeout> | null = null

export const normalizeMarketingPagePath = (value: string): string => {
  const trimmedValue = String(value || '').trim()
  if (trimmedValue.length <= MARKETING_PAGE_PATH_MAX_LENGTH) {
    return trimmedValue
  }

  const pathnameOnly = trimmedValue.split('?')[0]
  if (pathnameOnly && pathnameOnly.length <= MARKETING_PAGE_PATH_MAX_LENGTH) {
    return pathnameOnly
  }

  return trimmedValue.slice(0, MARKETING_PAGE_PATH_MAX_LENGTH)
}

const generateMarketingId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `mk_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`
}

export const generateOperationRunToken = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `run_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`
}

const resolveStorage = <T extends Storage>(factory: () => T | null): T | null => {
  try {
    return factory()
  } catch (_error) {
    return null
  }
}

export const getMarketingVisitorId = () => {
  if (typeof window === 'undefined') {
    return ''
  }
  const storage = resolveStorage(() => window.localStorage)
  if (!storage) {
    return ''
  }
  const cached = storage.getItem(MARKETING_VISITOR_ID_STORAGE_KEY)
  if (cached) {
    return cached
  }
  const nextId = generateMarketingId()
  try {
    storage.setItem(MARKETING_VISITOR_ID_STORAGE_KEY, nextId)
  } catch (_error) {
    return ''
  }
  return nextId
}

export const getMarketingSessionId = () => {
  if (typeof window === 'undefined') {
    return ''
  }
  const storage = resolveStorage(() => window.sessionStorage)
  if (!storage) {
    return ''
  }
  const cached = storage.getItem(MARKETING_SESSION_ID_STORAGE_KEY)
  if (cached) {
    return cached
  }
  const nextId = generateMarketingId()
  try {
    storage.setItem(MARKETING_SESSION_ID_STORAGE_KEY, nextId)
  } catch (_error) {
    return ''
  }
  return nextId
}

export async function sendMarketingEvents(
  events: MarketingEventPayload[],
  options: SendMarketingEventOptions = {}
): Promise<boolean> {
  if (typeof window === 'undefined' || events.length === 0) {
    return false
  }
  const baseUrl = resolveApiBaseUrl(options.apiBaseUrl)
  const endpoint = `${baseUrl}/api/v1/public/analytics/events`

  const normalizedEvents = events
    .map((event) => {
      const pathWithQuery = `${window.location.pathname}${window.location.search}`
      return {
        event_name: event.event_name,
        event_source: event.event_source || 'webapp',
        page_url: event.page_url || window.location.href,
        page_path: normalizeMarketingPagePath(event.page_path || pathWithQuery),
        referrer_url: event.referrer_url || document.referrer || undefined,
        visitor_id: event.visitor_id || getMarketingVisitorId() || undefined,
        session_id: event.session_id || getMarketingSessionId() || undefined,
        language: event.language || document.documentElement.lang || navigator.language || undefined,
        occurred_at: event.occurred_at || new Date().toISOString(),
        utm_source: event.utm_source,
        utm_medium: event.utm_medium,
        utm_campaign: event.utm_campaign,
        utm_term: event.utm_term,
        utm_content: event.utm_content,
        properties: event.properties || {}
      }
    })
    .filter((event) => Boolean(event.event_name))

  if (normalizedEvents.length === 0) {
    return false
  }

  const body = JSON.stringify({ events: normalizedEvents })

  if (options.transport === 'beacon' && typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
    const beaconPayload = new Blob([body], { type: 'application/json' })
    return navigator.sendBeacon(endpoint, beaconPayload)
  }

  const token = resolveAuthToken()
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  if (token) {
    headers.Authorization = `Bearer ${token}`
  }

  try {
    const response = await fetch(endpoint, {
      method: 'POST',
      headers,
      body,
      keepalive: options.keepalive ?? false,
    })
    return response.ok
  } catch (_error) {
    return false
  }
}

const clearMarketingBatchTimer = () => {
  if (pendingMarketingBatchTimer !== null) {
    clearTimeout(pendingMarketingBatchTimer)
    pendingMarketingBatchTimer = null
  }
}

const consumePendingMarketingBatch = (): PendingMarketingBatch | null => {
  const current = pendingMarketingBatch
  pendingMarketingBatch = null
  clearMarketingBatchTimer()
  return current
}

const flushPendingMarketingBatch = async (): Promise<boolean> => {
  const batch = consumePendingMarketingBatch()
  if (!batch || batch.events.length === 0) {
    return false
  }
  const firstOptions = batch.options[0] || {}
  const mergedOptions: SendMarketingEventOptions = {
    apiBaseUrl: firstOptions.apiBaseUrl,
    transport: 'fetch',
    keepalive: batch.options.some((option) => Boolean(option.keepalive)),
  }
  const ok = await sendMarketingEvents(batch.events, mergedOptions)
  batch.resolvers.forEach((resolver) => resolver(ok))
  return ok
}

const schedulePendingMarketingBatchFlush = () => {
  if (pendingMarketingBatchTimer !== null) {
    return
  }
  pendingMarketingBatchTimer = setTimeout(() => {
    void flushPendingMarketingBatch()
  }, MARKETING_EVENT_BATCH_WINDOW_MS)
}

export async function sendMarketingEvent(
  event: MarketingEventPayload,
  options: SendMarketingEventOptions = {}
): Promise<boolean> {
  if (options.transport === 'beacon') {
    return await sendMarketingEvents([event], options)
  }

  return await new Promise<boolean>((resolve) => {
    const nextBatch = pendingMarketingBatch ?? {
      events: [],
      options: [],
      resolvers: [],
    }
    nextBatch.events.push(event)
    nextBatch.options.push(options)
    nextBatch.resolvers.push(resolve)
    pendingMarketingBatch = nextBatch
    schedulePendingMarketingBatchFlush()
  })
}

export const __resetPendingMarketingBatchForTests = () => {
  pendingMarketingBatch = null
  clearMarketingBatchTimer()
}

export const __flushPendingMarketingBatchForTests = async (): Promise<boolean> => {
  return await flushPendingMarketingBatch()
}

export type PublicAppConfigLinks = {
  tutorial_url: string
  user_guide_url: string
  help_center_url: string
  privacy_url: string
  terms_url: string
  security_whitepaper_url: string
  copyright_whitepaper_url: string
  legal_certificates_url: string
  task_center_batch_template_url: string
}

export type PublicSupportChannel = {
  key: string
  title: string
  subtitle: string
  value: string
  badge?: string | null
}

export type PublicSupportGroup = {
  title: string
  subtitle: string
}

export type PublicSupportConfig = {
  headline: string
  subtitle: string
  benefit_title: string
  benefit_desc: string
  channels: PublicSupportChannel[]
  group: PublicSupportGroup
}

export type PublicAppConfigPayload = {
  success?: boolean
  version?: string
  links?: PublicAppConfigLinks
  support?: PublicSupportConfig
  feature_flags?: Record<string, boolean | string | number | null>
}

let publicAppConfigCache: PublicAppConfigPayload | null = null
let publicAppConfigCacheAt = 0
let publicAppConfigInflight: Promise<PublicAppConfigPayload | null> | null = null

const PUBLIC_APP_CONFIG_TTL_MS = 5 * 60 * 1000

export async function fetchPublicAppConfig(input?: { apiBaseUrl?: string; force?: boolean }) {
  if (typeof fetch === 'undefined') {
    return null
  }
  const now = Date.now()
  const shouldUseCache =
    !input?.force &&
    publicAppConfigCache &&
    publicAppConfigCacheAt > 0 &&
    now - publicAppConfigCacheAt < PUBLIC_APP_CONFIG_TTL_MS
  if (shouldUseCache) {
    return publicAppConfigCache
  }

  if (publicAppConfigInflight) {
    return await publicAppConfigInflight
  }

  publicAppConfigInflight = (async () => {
    try {
      const payload = await fetchAppShellJsonWithLocalCache<PublicAppConfigPayload>({
        resourceName: 'public:app-config',
        path: '/api/v1/public/app-config',
        ttlMs: APP_SHELL_PUBLIC_APP_CONFIG_TTL_MS,
        entityType: 'public-app-config',
        apiBaseUrl: input?.apiBaseUrl,
        forceRefresh: input?.force,
        sensitivity: 'low',
        publicResource: true,
        publicScope: 'app-config',
        selectPayload: (value) => (value && typeof value === 'object' ? value as PublicAppConfigPayload : null),
      })
      if (!payload) {
        return null
      }
      publicAppConfigCache = payload
      publicAppConfigCacheAt = Date.now()
      return payload
    } catch (error) {
      return null
    } finally {
      publicAppConfigInflight = null
    }
  })()

  return await publicAppConfigInflight
}

export function resolveAbsoluteUrl(urlOrPath: string, apiBaseUrl?: string) {
  if (!urlOrPath) {
    return ''
  }
  const trimmed = urlOrPath.trim()
  if (!trimmed) {
    return ''
  }
  let resolved = trimmed
  if (trimmed.startsWith('/')) {
    const baseUrl = resolveApiBaseUrl(apiBaseUrl)
    resolved = `${baseUrl}${trimmed}`
  }

  if (typeof window !== 'undefined' && (resolved.startsWith('http://') || resolved.startsWith('https://'))) {
    try {
      const parsed = new URL(resolved)
      const legalPaths = ['/api/v1/public/legal/privacy', '/api/v1/public/legal/terms']
      if (legalPaths.includes(parsed.pathname) && !parsed.searchParams.has('return_url')) {
        parsed.searchParams.set('return_url', window.location.href)
        resolved = parsed.toString()
      }
    } catch (error) {
      // ignore
    }
  }

  return resolved
}

const SOURCE_URL_ID_ONLY_PATTERN = /^\d+$/
const OZON_PRODUCT_PATH_PATTERN = /\/product\/[^/?#]*?(\d+)(?:[/?#]|$)/i
const OZON_SELLER_EDIT_PATH_PATTERN = /\/app\/products\/(\d+)\/edit\/general-info(?:[/?#]|$)/i

const truncateMiddleText = (value: string, maxLength: number): string => {
  if (maxLength <= 3 || value.length <= maxLength) {
    return value
  }
  const leftLength = Math.ceil((maxLength - 1) / 2)
  const rightLength = Math.floor((maxLength - 1) / 2)
  return `${value.slice(0, leftLength)}…${value.slice(value.length - rightLength)}`
}

export const resolveSourceUrl = (value: unknown): string => {
  const raw = String(value ?? '').trim()
  if (!raw) {
    return ''
  }

  if (SOURCE_URL_ID_ONLY_PATTERN.test(raw)) {
    return `https://detail.1688.com/offer/${raw}.html`
  }

  const withProtocol = raw.startsWith('//')
    ? `https:${raw}`
    : raw.startsWith('detail.1688.com/')
      ? `https://${raw}`
      : raw

  try {
    const parsed = new URL(withProtocol)
    const protocol = parsed.protocol.toLowerCase()
    if (protocol !== 'http:' && protocol !== 'https:') {
      return ''
    }
    return parsed.toString()
  } catch (error) {
    return ''
  }
}

export const formatSourceUrlPreview = (value: unknown, maxLength: number = 60): string => {
  const resolved = resolveSourceUrl(value)
  if (!resolved) {
    return ''
  }
  try {
    const parsed = new URL(resolved)
    const composed = `${parsed.host}${parsed.pathname}${parsed.search}${parsed.hash}` || resolved
    return truncateMiddleText(composed, maxLength)
  } catch (error) {
    return truncateMiddleText(resolved, maxLength)
  }
}

export const extractSourceProductId = (value: unknown): string => {
  const resolved = resolveSourceUrl(value)
  if (!resolved) {
    return ''
  }

  try {
    const parsed = new URL(resolved)
    const queryCandidates = ['offer_id', 'offerId', 'product_id', 'productId', 'id']
    for (const key of queryCandidates) {
      const candidate = parsed.searchParams.get(key)
      if (candidate && SOURCE_URL_ID_ONLY_PATTERN.test(candidate.trim())) {
        return candidate.trim()
      }
    }

    const pathCandidates = [
      /\/offer\/(\d+)(?:\.html)?/i,
      /\/item\/(\d+)(?:\.html)?/i,
    ]

    for (const pattern of pathCandidates) {
      const matched = parsed.pathname.match(pattern)
      if (matched?.[1]) {
        return matched[1]
      }
    }
  } catch (error) {
    return ''
  }

  return ''
}

export const extractOzonProductId = (value: unknown): string => {
  const raw = String(value ?? '').trim()
  if (!raw) {
    return ''
  }

  if (SOURCE_URL_ID_ONLY_PATTERN.test(raw)) {
    return raw
  }

  try {
    const parsed = new URL(raw)
    const path = parsed.pathname || ''
    const sellerMatched = path.match(OZON_SELLER_EDIT_PATH_PATTERN)
    if (sellerMatched?.[1]) {
      return sellerMatched[1]
    }
    const productMatched = path.match(OZON_PRODUCT_PATH_PATTERN)
    if (productMatched?.[1]) {
      return productMatched[1]
    }
    const queryMatched = parsed.searchParams.get('product_id') || parsed.searchParams.get('productId')
    if (queryMatched && SOURCE_URL_ID_ONLY_PATTERN.test(queryMatched.trim())) {
      return queryMatched.trim()
    }
  } catch (error) {
    return ''
  }

  return ''
}

export const buildOzonSellerProductEditUrl = (productId: unknown): string => {
  const resolvedProductId = extractOzonProductId(productId)
  if (!resolvedProductId) {
    return ''
  }
  return `https://seller.ozon.ru/app/products/${resolvedProductId}/edit/general-info`
}

export const openSourceUrlInNewTab = (value: unknown): boolean => {
  const resolved = resolveSourceUrl(value)
  if (!resolved || typeof window === 'undefined') {
    return false
  }
  window.open(resolved, '_blank', 'noopener,noreferrer')
  return true
}

export type ExtensionGuideSource = 'header' | 'workspace' | 'import'

export type ExtensionGuideOpenDetail = {
  source?: ExtensionGuideSource
  forceRestart?: boolean
}

export const EXTENSION_GUIDE_OPEN_EVENT = 'extension-guide:open'

export function openExtensionInstallGuide(detail?: ExtensionGuideOpenDetail) {
  if (typeof window === 'undefined') {
    return
  }
  window.dispatchEvent(new CustomEvent<ExtensionGuideOpenDetail>(EXTENSION_GUIDE_OPEN_EVENT, { detail }))
}

export const ANNOUNCEMENT_CENTER_OPEN_EVENT = 'announcement-center:open'

export function openAnnouncementCenter() {
  if (typeof window === 'undefined') {
    return
  }
  window.dispatchEvent(new Event(ANNOUNCEMENT_CENTER_OPEN_EVENT))
}

export function splitOptionValues(value: string) {
  if (!value) return []
  return Array.from(
    new Set(
      value
        .split(/[\s,，;/|、]+/)
        .map((item) => item.trim())
        .filter(Boolean)
    )
  )
}

function extractValuesFromSkuName(name: string) {
  const values: string[] = []
  const patterns = [/【([^】]+)】/g, /\[([^\]]+)\]/g, /\(([^)]+)\)/g]
  for (const pattern of patterns) {
    let match = pattern.exec(name)
    while (match) {
      values.push(...splitOptionValues(match[1]))
      match = pattern.exec(name)
    }
  }
  return values
}

function normalizeNumber(value: unknown) {
  if (typeof value === 'number' && !Number.isNaN(value)) return value
  if (typeof value === 'string') {
    const cleaned = value.replace(/[^\d.]+/g, '')
    const parsed = Number(cleaned)
    if (!Number.isNaN(parsed)) return parsed
  }
  return null
}

function formatDimensionValue(mmValue: number) {
  return Math.round(mmValue).toLocaleString()
}

function formatWeight(grams: number) {
  return `${Math.round(grams).toLocaleString()} g`
}

function humanizeKey(key: string) {
  return key
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim()
    .split(' ')
    .map((part) => (part ? part.charAt(0).toUpperCase() + part.slice(1) : ''))
    .join(' ')
}

function stripUnitSuffix(key: string) {
  const cleaned = key.replace(/_(mm|cm|m|g|kg)$/i, '').replace(/(mm|cm|m|g|kg)$/i, '')
  return cleaned || key
}

export type SkuInsights = {
  colors: string[]
  sizes: string[]
  price: number | null
  stock: number | null
  weightText: string | null
  dimensionsText: string | null
}

export function extractSkuInsights(extractedData?: Record<string, any>): SkuInsights {
  const colors = new Set<string>()
  const sizes = new Set<string>()
  let price: number | null = null
  let stock: number | null = null
  let weightText: string | null = null
  let dimensionsText: string | null = null

  if (!extractedData) {
    return { colors: [], sizes: [], price, stock, weightText, dimensionsText }
  }

  const selectPrice = (value: unknown) => {
    const parsed = normalizeNumber(value)
    if (parsed !== null) {
      price = parsed
    }
  }

  if (extractedData.selected_sku?.price !== undefined) {
    selectPrice(extractedData.selected_sku.price)
  }

  if (price === null && extractedData.price !== undefined) {
    selectPrice(extractedData.price)
  }

  if (price === null && typeof extractedData.price_range === 'string') {
    selectPrice(extractedData.price_range)
  }

  if (extractedData.selected_sku?.stock !== undefined) {
    const parsed = normalizeNumber(extractedData.selected_sku.stock)
    if (parsed !== null) {
      stock = parsed
    }
  }

  const attributeEntries = extractedData.parsed_attributes
    ? Object.entries(extractedData.parsed_attributes)
    : []

  attributeEntries.forEach(([key, value]) => {
    const val = typeof value === 'string' ? value : ''
    if (!val) return
    if (/颜色|color|colour/i.test(key)) {
      splitOptionValues(val).forEach((item) => colors.add(item))
    }
    if (/尺码|尺寸|size/i.test(key)) {
      splitOptionValues(val).forEach((item) => sizes.add(item))
    }
  })

  if (Array.isArray(extractedData.parsed_skus)) {
    extractedData.parsed_skus.forEach((sku: Record<string, any>) => {
      if (price === null && sku?.price !== undefined) {
        selectPrice(sku.price)
      }

      const skuStock = normalizeNumber(sku?.stock)
      if (skuStock !== null) {
        stock = stock === null ? skuStock : Math.max(stock, skuStock)
      }

      const attributes = sku?.attributes && typeof sku.attributes === 'object' ? sku.attributes : null
      if (attributes) {
        Object.entries(attributes).forEach(([key, value]) => {
          const val = typeof value === 'string' ? value : ''
          if (!val) return
          if (/颜色|color|colour/i.test(key)) {
            splitOptionValues(val).forEach((item) => colors.add(item))
          }
          if (/尺码|尺寸|size/i.test(key)) {
            splitOptionValues(val).forEach((item) => sizes.add(item))
          }
        })
      }

      const variantType = sku?.variant_type || sku?.variantType || sku?.type
      const variantValue = sku?.variant_value || sku?.variantValue || sku?.value
      if (typeof variantType === 'string' && typeof variantValue === 'string') {
        if (/颜色|color|colour/i.test(variantType)) {
          splitOptionValues(variantValue).forEach((item) => colors.add(item))
        }
        if (/尺码|尺寸|size/i.test(variantType)) {
          splitOptionValues(variantValue).forEach((item) => sizes.add(item))
        }
      } else if (typeof sku?.name === 'string') {
        const values = extractValuesFromSkuName(sku.name)
        values.forEach((item) => {
          if (!colors.has(item) && sizes.size === 0) {
            colors.add(item)
          } else {
            sizes.add(item)
          }
        })
      }
    })
  }

  const dimensions = extractedData.parsed_dimensions
  if (dimensions && typeof dimensions === 'object') {
    const lengthMm = normalizeNumber(dimensions.length_mm)
    const widthMm = normalizeNumber(dimensions.width_mm)
    const heightMm = normalizeNumber(dimensions.height_mm)
    const weightG = normalizeNumber(dimensions.weight_g)

    if (lengthMm !== null && widthMm !== null && heightMm !== null) {
      dimensionsText = `${formatDimensionValue(lengthMm)} x ${formatDimensionValue(widthMm)} x ${formatDimensionValue(heightMm)} mm`
    }
    if (weightG !== null) {
      weightText = formatWeight(weightG)
    }
  }

  return {
    colors: Array.from(colors),
    sizes: Array.from(sizes),
    price,
    stock,
    weightText,
    dimensionsText
  }
}

export function resolvePreviewImageUrl(image: unknown): string {
  if (typeof image === 'string') {
    return image.trim()
  }
  if (!image || typeof image !== 'object') {
    return ''
  }
  const record = image as Record<string, unknown>
  const candidates = [
    record.storage_path,
    record.original_url,
    record.originalUrl,
    record.url,
    record.image_url,
    record.imageUrl,
    record.src
  ]
  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.trim()) {
      return candidate.trim()
    }
  }
  return ''
}

export function deriveImageThumbnailUrl(url: string): string {
  const cleanUrl = String(url ?? '').trim()
  if (!cleanUrl) {
    return ''
  }
  const [withoutHash, hashPart = ''] = cleanUrl.split('#', 2)
  const [pathPart, queryPart = ''] = withoutHash.split('?', 2)
  if (!pathPart) {
    return cleanUrl
  }

  const lastSlashIndex = pathPart.lastIndexOf('/')
  const fileName = lastSlashIndex >= 0 ? pathPart.slice(lastSlashIndex + 1) : pathPart
  if (!fileName || /_thumb\.[^./]+$/i.test(fileName)) {
    return cleanUrl
  }

  const extensionMatch = fileName.match(/\.(avif|bmp|gif|jpe?g|png|webp)$/i)
  if (!extensionMatch) {
    return cleanUrl
  }

  const ext = extensionMatch[0]
  const baseName = fileName.slice(0, -ext.length)
  const nextFileName = `${baseName}_thumb${ext}`
  const nextPath = lastSlashIndex >= 0 ? `${pathPart.slice(0, lastSlashIndex + 1)}${nextFileName}` : nextFileName
  const nextQuery = queryPart ? `?${queryPart}` : ''
  const nextHash = hashPart ? `#${hashPart}` : ''
  return `${nextPath}${nextQuery}${nextHash}`
}

export function deriveImageOriginalUrlFromThumbnail(url: string): string {
  const cleanUrl = String(url ?? '').trim()
  if (!cleanUrl) {
    return ''
  }

  const [withoutHash, hashPart = ''] = cleanUrl.split('#', 2)
  const [pathPart, queryPart = ''] = withoutHash.split('?', 2)
  if (!pathPart) {
    return cleanUrl
  }

  const lastSlashIndex = pathPart.lastIndexOf('/')
  const fileName = lastSlashIndex >= 0 ? pathPart.slice(lastSlashIndex + 1) : pathPart
  if (!fileName) {
    return cleanUrl
  }

  const nextFileName = fileName.replace(/_thumb(\.(avif|bmp|gif|jpe?g|png|webp))$/i, '$1')
  if (nextFileName === fileName) {
    return cleanUrl
  }

  const nextPath = lastSlashIndex >= 0
    ? `${pathPart.slice(0, lastSlashIndex + 1)}${nextFileName}`
    : nextFileName
  const nextQuery = queryPart ? `?${queryPart}` : ''
  const nextHash = hashPart ? `#${hashPart}` : ''
  return `${nextPath}${nextQuery}${nextHash}`
}

export function resolveVersionedImageUrl(url: string, version?: string | number | null): string {
  const cleanUrl = String(url ?? '').trim()
  if (!cleanUrl) {
    return ''
  }

  const cleanVersion = String(version ?? '').trim()
  if (!cleanVersion) {
    return cleanUrl
  }

  const [withoutHash, hashPart = ''] = cleanUrl.split('#', 2)
  const [pathPart, queryPart = ''] = withoutHash.split('?', 2)
  if (!pathPart) {
    return cleanUrl
  }

  const params = new URLSearchParams(queryPart)
  params.delete('v')
  params.append('v', cleanVersion)
  const nextQuery = params.toString()
  const querySuffix = nextQuery ? `?${nextQuery}` : ''
  const hashSuffix = hashPart ? `#${hashPart}` : ''
  return `${pathPart}${querySuffix}${hashSuffix}`
}

export function resolveVersionedImageThumbnailUrl(url: string, version?: string | number | null): string {
  const thumbnailUrl = deriveImageThumbnailUrl(url)
  if (!thumbnailUrl) {
    return ''
  }

  return resolveVersionedImageUrl(thumbnailUrl, version)
}

const normalizeImageSlotIdentity = (value: string): string => {
  return String(value ?? '').trim().split('?')[0]
}

const resolveImageSlotVersion = (plan?: Partial<ImageSlot> | null): string => {
  if (!plan || typeof plan !== 'object') {
    return ''
  }
  const candidate = plan.generated_version ?? plan.generated_at ?? ''
  if (candidate === null || candidate === undefined) {
    return ''
  }
  return String(candidate).trim()
}

export function resolvePreviewImageDisplayUrl(
  previewUrl: string,
  imageSlots?: ImageSlot[] | null,
  fallbackVersion?: string | number | null
): string {
  const cleanPreviewUrl = String(previewUrl ?? '').trim()
  if (!cleanPreviewUrl || !Array.isArray(imageSlots) || imageSlots.length <= 0) {
    return cleanPreviewUrl
  }

  const previewIdentity = normalizeImageSlotIdentity(cleanPreviewUrl)
  if (!previewIdentity) {
    return cleanPreviewUrl
  }

  for (const plan of imageSlots) {
    if (!plan || typeof plan !== 'object') {
      continue
    }

    const rawGeneratedUrl = String(plan.generated_raw_url ?? plan.generated_url ?? '').trim()
    if (!rawGeneratedUrl) {
      continue
    }

    const identities = [
      normalizeImageSlotIdentity(String(plan.generated_url ?? '')),
      normalizeImageSlotIdentity(rawGeneratedUrl),
    ]
    if (!identities.includes(previewIdentity)) {
      continue
    }

    const version = resolveImageSlotVersion(plan) || String(fallbackVersion ?? '').trim()
    return resolveVersionedImageUrl(rawGeneratedUrl, version)
  }

  return cleanPreviewUrl
}

function normalizePreviewImageKey(image: unknown): string {
  const url = resolvePreviewImageUrl(image)
  if (!url) {
    return ''
  }
  return url
    .split('?')[0]
    .split('#')[0]
    .replace(/^https?:\/\//i, '')
    .toLowerCase()
}

export function normalizeReferenceSourceKey(value: unknown): string {
  const normalized = String(value ?? '').trim().toLowerCase()
  if (!normalized) {
    return 'unknown'
  }

  switch (normalized) {
    case 'manual':
    case 'auto_sku_match':
    case 'sku_image_url':
    case 'selected_sku_image_indices':
    case 'selected_sku':
    case 'selected_anchor':
    case 'sku':
    case 'main':
    case 'detail':
    case 'collection':
    case 'extracted':
    case 'legacy':
    case 'none':
    case 'unknown':
      return normalized
    default:
      return normalized
  }
}

export function buildPreviewImages(preview: { images?: Array<{ storage_path?: string; original_url?: string; url?: string }>; extracted_data?: Record<string, any> }) {
  const images = Array.isArray(preview?.images)
    ? preview.images
      .map((img) => resolvePreviewImageUrl(img))
      .filter((item) => item.length > 0)
    : []

  if (images.length > 0) {
    return images
  }

  const extractedImages = preview?.extracted_data?.images
  if (Array.isArray(extractedImages) && extractedImages.length > 0) {
    return extractedImages
      .map((img) => resolvePreviewImageUrl(img))
      .filter((item) => item.length > 0)
  }

  const skus = Array.isArray(preview?.extracted_data?.parsed_skus) ? preview.extracted_data.parsed_skus : []
  const skuImages = skus
    .map((sku: Record<string, any>) => sku?.image_url || sku?.imageUrl || sku?.image)
    .filter(Boolean)

  if (skuImages.length > 0) {
    return skuImages
  }

  return []
}

const hasNonEmptyPreviewText = (value: unknown): boolean => {
  if (Array.isArray(value)) {
    return value.some((item) => hasNonEmptyPreviewText(item))
  }
  return String(value ?? '').trim().length > 0
}

const hasPreviewAttributes = (value: unknown): boolean => {
  if (Array.isArray(value)) {
    return value.some((item) => {
      if (!item || typeof item !== 'object' || Array.isArray(item)) {
        return hasNonEmptyPreviewText(item)
      }
      const record = item as Record<string, unknown>
      return hasNonEmptyPreviewText(record.value)
        || hasNonEmptyPreviewText(record.values)
        || hasNonEmptyPreviewText(record.text)
        || hasNonEmptyPreviewText(record.name)
    })
  }

  if (!value || typeof value !== 'object') {
    return false
  }

  return Object.values(value as Record<string, unknown>).some((item) => hasNonEmptyPreviewText(item))
}

export function isUsableOzonPreviewPayload(payload: unknown): payload is Record<string, any> {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    return false
  }

  const record = payload as Record<string, unknown>
  const hasName = hasNonEmptyPreviewText(record.name)
  if (!hasName) {
    return false
  }

  const hasDescription = hasNonEmptyPreviewText(record.description)
  const hasTags = hasNonEmptyPreviewText(record.tags)
  const hasAttributes = hasPreviewAttributes(record.attributes)
  const hasImages = Array.isArray(record.images)
    ? record.images.some((item) => resolvePreviewImageUrl(item).trim().length > 0)
    : hasNonEmptyPreviewText(record.primary_image)
  const hasPrice = hasNonEmptyPreviewText(record.price) || hasNonEmptyPreviewText(record.old_price)

  return hasDescription || hasTags || hasAttributes || hasImages || hasPrice
}

export function resolveUsableOzonPreviewPayload(input: {
  platform?: unknown
  adapterOutput?: Record<string, any> | null
  uploadPreview?: Record<string, any> | null
  uploadData?: Record<string, any> | null
}): Record<string, any> | null {
  const platform = String(input.adapterOutput?.platform ?? input.platform ?? '').trim().toLowerCase()
  if (platform !== 'ozon') {
    return null
  }

  const candidates = [input.uploadPreview, input.uploadData, input.adapterOutput?.payload]
  for (const candidate of candidates) {
    if (isUsableOzonPreviewPayload(candidate)) {
      return candidate
    }
  }

  return null
}

const toPositiveNumber = (value: unknown): number | null => {
  const parsed = Number(value)
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return null
  }
  return parsed
}

const toNonNegativeNumber = (value: unknown): number | null => {
  const parsed = Number(value)
  if (!Number.isFinite(parsed) || parsed < 0) {
    return null
  }
  return parsed
}

const toBoundedRatio = (value: unknown, fallback: number): number => {
  const parsed = Number(value)
  if (!Number.isFinite(parsed) || parsed <= 0 || parsed > 1) {
    return fallback
  }
  return parsed
}

export function normalizePricingResultSnapshot(raw: unknown): Record<string, any> | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return null
  }
  const source = raw as Record<string, any>
  const nested = source.pricing_result
  if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
    return {
      ...(nested as Record<string, any>),
      ...Object.entries(source).reduce<Record<string, any>>((acc, [key, value]) => {
        if (key !== 'pricing_result' && !(key in (nested as Record<string, any>))) {
          acc[key] = value
        }
        return acc
      }, {})
    }
  }
  return source
}

export function resolvePreviewPricingSnapshot(input: {
  collectionPricingState?: unknown
  variantPricingSummary?: unknown
  preferVariant?: boolean
}): Record<string, any> | null {
  if (input.preferVariant) {
    const variantSummary = input.variantPricingSummary
    if (variantSummary && typeof variantSummary === 'object' && !Array.isArray(variantSummary)) {
      const variantRecord = variantSummary as Record<string, any>
      return normalizePricingResultSnapshot(variantRecord.raw_result ?? variantRecord)
    }
  }
  return normalizePricingResultSnapshot(input.collectionPricingState)
}

export type OzonPricingDisplayState = {
  snapshot: Record<string, any> | null
  listingPriceCny: number | null
  buyerPayPriceCny: number | null
  listingPriceRub: number | null
  buyerPayPriceRub: number | null
  purchaseCostCny: number | null
  domesticShippingCny: number | null
  labelingFeeCny: number | null
  uniShippingCny: number | null
  platformFeeTotalCny: number | null
  withdrawalFeeCny: number | null
  advertisingCostCny: number | null
  returnReserveCny: number | null
  upfrontCostCny: number | null
  postSaleDeductionCny: number | null
  totalCostCny: number | null
  profitCny: number | null
  roi: number | null
  exchangeRate: number | null
  snapshotListingPriceCny: number | null
  snapshotBuyerPayPriceCny: number | null
  listingPriceSource: 'payload' | 'snapshot' | 'none'
  payFactor: number
  discountRate: number
  isSnapshotOutdated: boolean
}

export function resolveOzonPricingDisplayState(input: {
  pricingResult?: unknown
  payloadPrice?: unknown
  payFactorFallback?: number
  staleTolerance?: number
}): OzonPricingDisplayState {
  const payFactorFallback = toBoundedRatio(input.payFactorFallback ?? 0.6, 0.6)
  const staleTolerance = Number.isFinite(Number(input.staleTolerance)) && Number(input.staleTolerance) > 0
    ? Number(input.staleTolerance)
    : 0.01
  const snapshot = normalizePricingResultSnapshot(input.pricingResult)
  const snapshotMeta = snapshot?.meta && typeof snapshot.meta === 'object'
    ? snapshot.meta as Record<string, any>
    : null
  const snapshotWriteReason = String(snapshotMeta?.write_reason ?? '').trim().toLowerCase()
  const isSnapshotMetricsStale = Boolean(snapshotMeta?.pricing_metrics_stale)
    || snapshotWriteReason === 'preview_draft_patch'
  const payloadListingPrice = toPositiveNumber(input.payloadPrice)
  const snapshotListingPrice = toPositiveNumber(snapshot?.listing_price_cny)
    ?? toPositiveNumber(snapshot?.price)
  const payFactor = toBoundedRatio(
    snapshot?.discount_pay_factor ?? snapshot?.discount_policy?.pay_factor,
    payFactorFallback
  )
  const discountRate = toBoundedRatio(
    snapshot?.fixed_discount_rate ?? snapshot?.discount_policy?.fixed_discount_rate,
    1 - payFactor
  )
  const snapshotBuyerPayPrice = toPositiveNumber(snapshot?.target_price_cny)
    ?? (snapshotListingPrice !== null ? Number((snapshotListingPrice * payFactor).toFixed(2)) : null)
  const snapshotExchangeRate = toPositiveNumber(snapshot?.exchange_rate)
    ?? toPositiveNumber(snapshot?.cost_breakdown?.exchange_rate)
  const snapshotListingPriceRub = toPositiveNumber(snapshot?.listing_price_rub)
    ?? toPositiveNumber(snapshot?.price_rub)
    ?? (snapshotListingPrice !== null && snapshotExchangeRate !== null
      ? Number((snapshotListingPrice * snapshotExchangeRate).toFixed(2))
      : null)
  const snapshotBuyerPayPriceRub = toPositiveNumber(snapshot?.target_price_rub)
    ?? toPositiveNumber(snapshot?.selling_price_rub)
    ?? (snapshotBuyerPayPrice !== null && snapshotExchangeRate !== null
      ? Number((snapshotBuyerPayPrice * snapshotExchangeRate).toFixed(2))
      : null)
  const listingPrice = payloadListingPrice ?? snapshotListingPrice
  const buyerPayPrice = toPositiveNumber(snapshot?.target_price_cny)
    ?? (listingPrice !== null ? Number((listingPrice * payFactor).toFixed(2)) : null)
  const exchangeRate = snapshotExchangeRate
    ?? (snapshotBuyerPayPriceRub !== null && buyerPayPrice !== null && buyerPayPrice > 0
      ? Number((snapshotBuyerPayPriceRub / buyerPayPrice).toFixed(4))
      : null)
  const listingPriceRub = snapshotListingPriceRub
    ?? (listingPrice !== null && exchangeRate !== null
      ? Number((listingPrice * exchangeRate).toFixed(2))
      : null)
  const buyerPayPriceRub = snapshotBuyerPayPriceRub
    ?? (buyerPayPrice !== null && exchangeRate !== null
      ? Number((buyerPayPrice * exchangeRate).toFixed(2))
      : null)
  const costBreakdown = snapshot?.cost_breakdown && typeof snapshot.cost_breakdown === 'object'
    ? snapshot.cost_breakdown as Record<string, any>
    : null
  const purchaseCostCny = toNonNegativeNumber(costBreakdown?.purchase_cost_cny)
  const domesticShippingCny = toNonNegativeNumber(costBreakdown?.domestic_shipping_cny)
  const labelingFeeCny = toNonNegativeNumber(costBreakdown?.labeling_fee_cny)
  const uniShippingCny = toNonNegativeNumber(costBreakdown?.uni_shipping_cny)
  const platformFeeTotalRub = toNonNegativeNumber(costBreakdown?.platform_fee_total_rub)
  const platformFeeTotalCny = toNonNegativeNumber(costBreakdown?.platform_fee_total_cny)
    ?? (platformFeeTotalRub !== null && exchangeRate !== null && exchangeRate > 0
      ? Number((platformFeeTotalRub / exchangeRate).toFixed(2))
      : null)
  const withdrawalFeeCny = toNonNegativeNumber(costBreakdown?.withdrawal_fee_cny)
  const advertisingCostCny = toNonNegativeNumber(costBreakdown?.advertising_cost_cny)
  const returnReserveCny = toNonNegativeNumber(costBreakdown?.return_reserve_cny)
  const upfrontCostFromSnapshot = toNonNegativeNumber(costBreakdown?.upfront_cost_cny)
  const upfrontCostDerived = [purchaseCostCny, domesticShippingCny, labelingFeeCny].every((value) => value !== null)
    ? Number(((purchaseCostCny ?? 0) + (domesticShippingCny ?? 0) + (labelingFeeCny ?? 0)).toFixed(2))
    : null
  const upfrontCostCny = upfrontCostDerived ?? upfrontCostFromSnapshot
  const postSaleDeductionFromSnapshot = toNonNegativeNumber(costBreakdown?.post_sale_deduction_cny)
  const postSaleDeductionDerived = [
    uniShippingCny,
    platformFeeTotalCny,
    withdrawalFeeCny,
    advertisingCostCny,
    returnReserveCny
  ].some((value) => value !== null)
    ? Number(((
      (uniShippingCny ?? 0)
      + (platformFeeTotalCny ?? 0)
      + (withdrawalFeeCny ?? 0)
      + (advertisingCostCny ?? 0)
      + (returnReserveCny ?? 0)
    )).toFixed(2))
    : null
  const postSaleDeductionCny = postSaleDeductionFromSnapshot ?? postSaleDeductionDerived
  const totalCostCny = toNonNegativeNumber(costBreakdown?.total_cost_cny)
  const profitCny = isSnapshotMetricsStale
    ? null
    : toNonNegativeNumber(snapshot?.profit_cny)
      ?? (buyerPayPrice !== null && totalCostCny !== null
        ? Math.max(0, Number((buyerPayPrice - totalCostCny).toFixed(2)))
        : null)
  const roi = isSnapshotMetricsStale
    ? null
    : (Number.isFinite(Number(snapshot?.roi)) ? Number(snapshot?.roi) : null)
  const listingPriceSource: 'payload' | 'snapshot' | 'none' = payloadListingPrice !== null
    ? 'payload'
    : snapshotListingPrice !== null
      ? 'snapshot'
      : 'none'
  const isSnapshotOutdated = isSnapshotMetricsStale
    || (
      payloadListingPrice !== null
      && snapshotListingPrice !== null
      && Math.abs(payloadListingPrice - snapshotListingPrice) > staleTolerance
    )

  return {
    snapshot,
    listingPriceCny: listingPrice,
    buyerPayPriceCny: buyerPayPrice,
    listingPriceRub,
    buyerPayPriceRub,
    purchaseCostCny,
    domesticShippingCny,
    labelingFeeCny,
    uniShippingCny,
    platformFeeTotalCny,
    withdrawalFeeCny,
    advertisingCostCny,
    returnReserveCny,
    upfrontCostCny,
    postSaleDeductionCny,
    totalCostCny,
    profitCny,
    roi,
    exchangeRate,
    snapshotListingPriceCny: snapshotListingPrice,
    snapshotBuyerPayPriceCny: snapshotBuyerPayPrice,
    listingPriceSource,
    payFactor,
    discountRate,
    isSnapshotOutdated
  }
}

export function buildSkuAnchorCandidates(preview: {
  images?: Array<{ storage_path?: string; original_url?: string; url?: string; source_category?: string } | string>
  extracted_data?: Record<string, any> | null
}) {
  const extractedImages = Array.isArray(preview?.extracted_data?.images)
    ? preview.extracted_data.images
      .map((img, index) => ({ index, url: resolvePreviewImageUrl(img) }))
      .filter((item) => item.url.length > 0)
    : []

  if (extractedImages.length === 0) {
    return []
  }

  const skuImageKeys = new Set<string>()

  if (Array.isArray(preview?.images)) {
    preview.images.forEach((img) => {
      if (!img || typeof img !== 'object' || Array.isArray(img)) {
        return
      }
      const record = img as Record<string, unknown>
      const category = String(record.source_category ?? '').trim().toLowerCase()
      if (category !== 'sku') {
        return
      }
      const key = normalizePreviewImageKey(record)
      if (key) {
        skuImageKeys.add(key)
      }
    })
  }

  if (Array.isArray(preview?.extracted_data?.parsed_skus)) {
    preview.extracted_data.parsed_skus.forEach((sku) => {
      if (!sku || typeof sku !== 'object') {
        return
      }
      const record = sku as Record<string, unknown>
      const key = normalizePreviewImageKey(
        record.image_url || record.imageUrl || record.image || record.url || record.src
      )
      if (key) {
        skuImageKeys.add(key)
      }
    })
  }

  if (skuImageKeys.size > 0) {
    const filteredImages = extractedImages.filter((item) => skuImageKeys.has(normalizePreviewImageKey(item.url)))
    if (filteredImages.length > 0) {
      return filteredImages
    }
  }

  return extractedImages
}

export type SkuPreviewCandidate = {
  index: number
  url: string
}

export type SkuPreviewState = {
  currentCandidates: SkuPreviewCandidate[]
  selectedAnchorIndices: number[]
  previewImageUrl: string
}

export type ReferenceSelectionState = {
  selection_mode?: string
  reference_status?: string
  selected_sku: {
    key: string
    name: string
    locked: boolean
    source: string
  }
  reference_scope: {
    source: string
    locked: boolean
    image_indices: number[]
    image_ids?: string[]
    image_count: number
  }
  preferred_reference_image: {
    index: number | null
    image_id?: string | null
    source: string
    locked_by_user: boolean
  }
  final_reference_selection: {
    source: string
    mode: string
    selection_context_source: string
  }
}

const normalizePreviewImageIndices = (value: unknown): number[] => {
  if (!Array.isArray(value)) {
    return []
  }

  const deduped: number[] = []
  const seen = new Set<number>()
  value.forEach((item) => {
    const index = Number(item)
    if (!Number.isInteger(index) || index < 0 || seen.has(index)) {
      return
    }
    seen.add(index)
    deduped.push(index)
  })
  return deduped
}

const buildIndexedExtractedPreviewImages = (extractedData?: Record<string, any> | null): SkuPreviewCandidate[] => {
  if (!Array.isArray(extractedData?.images)) {
    return []
  }

  return extractedData.images
    .map((img, index) => ({ index, url: resolvePreviewImageUrl(img) }))
    .filter((item) => item.url.length > 0)
}

export function resolveSkuPreviewState(options: {
  images?: Array<{ storage_path?: string; original_url?: string; url?: string; source_category?: string } | string>
  extractedData?: Record<string, any> | null
  activeSkuImageUrl?: string
  selectedSkuImageIndices?: unknown
  selectedSkuAnchorImageIndices?: unknown
}): SkuPreviewState {
  const extractedImages = buildIndexedExtractedPreviewImages(options.extractedData)
  const activeSkuImageUrl = resolvePreviewImageUrl(options.activeSkuImageUrl)

  if (extractedImages.length === 0) {
    return {
      currentCandidates: [],
      selectedAnchorIndices: [],
      previewImageUrl: activeSkuImageUrl,
    }
  }

  const selectedSkuImageIndices = normalizePreviewImageIndices(options.selectedSkuImageIndices)
  let currentCandidates = selectedSkuImageIndices.length > 0
    ? extractedImages.filter((item) => selectedSkuImageIndices.includes(item.index))
    : []

  if (currentCandidates.length === 0 && activeSkuImageUrl) {
    const activeSkuImageKey = normalizePreviewImageKey(activeSkuImageUrl)
    if (activeSkuImageKey) {
      currentCandidates = extractedImages.filter((item) => normalizePreviewImageKey(item.url) === activeSkuImageKey)
    }
  }

  const currentCandidateIndexSet = new Set(currentCandidates.map((item) => item.index))
  const selectedAnchorIndices = normalizePreviewImageIndices(options.selectedSkuAnchorImageIndices)
    .filter((index) => currentCandidateIndexSet.has(index))

  let previewImageUrl = ''
  if (selectedAnchorIndices.length > 0) {
    previewImageUrl = currentCandidates.find((item) => item.index === selectedAnchorIndices[0])?.url || ''
  }
  if (!previewImageUrl && currentCandidates.length > 0) {
    previewImageUrl = currentCandidates[0].url
  }
  if (!previewImageUrl) {
    previewImageUrl = activeSkuImageUrl
  }

  return {
    currentCandidates,
    selectedAnchorIndices,
    previewImageUrl,
  }
}

const extractReferenceSelectedSkuKey = (extractedData?: Record<string, any> | null): string => {
  if (!extractedData || typeof extractedData !== 'object') {
    return ''
  }

  const selectedSkuKey = String(extractedData.selected_sku_key ?? '').trim()
  if (selectedSkuKey) {
    return selectedSkuKey
  }

  const selectedSku = extractedData.selected_sku
  if (!selectedSku || typeof selectedSku !== 'object') {
    return ''
  }

  for (const field of ['sku_id', 'skuId', 'id', 'offer_id', 'offerId', 'name', 'variant_value', 'variantValue']) {
    const candidate = String((selectedSku as Record<string, unknown>)[field] ?? '').trim()
    if (candidate) {
      return candidate
    }
  }

  return ''
}

const normalizeReferenceSelectionState = (value: unknown): ReferenceSelectionState | null => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null
  }

  const raw = value as Record<string, any>
  const selectedSku = raw.selected_sku && typeof raw.selected_sku === 'object' ? raw.selected_sku : {}
  const referenceScope = raw.reference_scope && typeof raw.reference_scope === 'object' ? raw.reference_scope : {}
  const preferredReferenceImage =
    raw.preferred_reference_image && typeof raw.preferred_reference_image === 'object'
      ? raw.preferred_reference_image
      : {}
  const finalReferenceSelection =
    raw.final_reference_selection && typeof raw.final_reference_selection === 'object'
      ? raw.final_reference_selection
      : {}

  const imageIndices = normalizePreviewImageIndices(referenceScope.image_indices)
  const imageIds = Array.isArray(referenceScope.image_ids)
    ? referenceScope.image_ids
      .map((item: unknown) => String(item ?? '').trim())
      .filter((item: string) => item.length > 0)
    : []
  const preferredReferenceIndex = Number(preferredReferenceImage.index)

  return {
    selection_mode: String(raw.selection_mode ?? '').trim().toLowerCase() || 'auto',
    reference_status: String(raw.reference_status ?? '').trim().toLowerCase() || 'idle',
    selected_sku: {
      key: String(selectedSku.key ?? '').trim(),
      name: String(selectedSku.name ?? '').trim(),
      locked: Boolean(selectedSku.locked),
      source: String(selectedSku.source ?? 'none').trim().toLowerCase() || 'none',
    },
    reference_scope: {
      source: String(referenceScope.source ?? 'none').trim().toLowerCase() || 'none',
      locked: Boolean(referenceScope.locked),
      image_indices: imageIndices,
      image_ids: imageIds,
      image_count: Number.isFinite(Number(referenceScope.image_count))
        ? Math.max(0, Math.round(Number(referenceScope.image_count)))
        : imageIds.length > 0 ? imageIds.length : imageIndices.length,
    },
    preferred_reference_image: {
      index: Number.isInteger(preferredReferenceIndex) && preferredReferenceIndex >= 0 ? preferredReferenceIndex : null,
      image_id: String(preferredReferenceImage.image_id ?? '').trim() || null,
      source: String(preferredReferenceImage.source ?? 'none').trim().toLowerCase() || 'none',
      locked_by_user: Boolean(preferredReferenceImage.locked_by_user),
    },
    final_reference_selection: {
      source: String(finalReferenceSelection.source ?? 'none').trim().toLowerCase() || 'none',
      mode: String(finalReferenceSelection.mode ?? 'none').trim().toLowerCase() || 'none',
      selection_context_source:
        String(finalReferenceSelection.selection_context_source ?? 'none').trim().toLowerCase() || 'none',
    },
  }
}

export function resolveReferenceSelectionState(options: {
  extractedData?: Record<string, any> | null
  skuReferenceImages?: Record<string, any> | null
  referenceSelectionState?: unknown
}): ReferenceSelectionState {
  const normalizedExisting = normalizeReferenceSelectionState(options.referenceSelectionState)
  if (normalizedExisting) {
    return normalizedExisting
  }

  const extractedData = options.extractedData && typeof options.extractedData === 'object' ? options.extractedData : null
  const skuReferenceImages =
    options.skuReferenceImages && typeof options.skuReferenceImages === 'object' ? options.skuReferenceImages : null

  const selectedSkuKey = extractReferenceSelectedSkuKey(extractedData)
  const selectedSku = extractedData?.selected_sku && typeof extractedData.selected_sku === 'object'
    ? extractedData.selected_sku
    : null
  const selectedSkuName = String((selectedSku as Record<string, unknown> | null)?.name ?? '').trim()
  const selectedSkuLocked = Boolean(extractedData?.selected_sku_locked)
  const selectedSkuSourceRaw = String(extractedData?.selected_sku_source ?? '').trim().toLowerCase()
  const selectedSkuSource = selectedSkuSourceRaw || (selectedSkuLocked ? 'user' : 'none')
  const selectionMode = String(extractedData?.selection_mode ?? '').trim().toLowerCase()
    || (selectedSkuLocked ? 'manual_sku' : 'auto')
  const referenceStatusFromPayload = String(skuReferenceImages?.reference_status ?? '').trim().toLowerCase()

  const referenceScopeImageIndices = normalizePreviewImageIndices(extractedData?.selected_sku_image_indices)
  const referenceScopeImageIds = Array.isArray(extractedData?.locked_scope_image_ids)
    ? extractedData.locked_scope_image_ids
      .map((item: unknown) => String(item ?? '').trim())
      .filter((item: string) => item.length > 0)
    : []
  const preferredReferenceImageIndices = normalizePreviewImageIndices(extractedData?.selected_sku_anchor_image_indices)
  const preferredReferenceSource = String(extractedData?.selected_sku_anchor_source ?? '').trim().toLowerCase() || 'none'
  const preferredReferenceImageId = String(extractedData?.preferred_reference_image_id ?? '').trim() || null
  const preferredReferenceIndex = preferredReferenceImageIndices[0] ?? null

  const selector = skuReferenceImages?.selector && typeof skuReferenceImages.selector === 'object'
    ? skuReferenceImages.selector
    : null
  const selectionContext = selector?.selection_context && typeof selector.selection_context === 'object'
    ? selector.selection_context
    : null
  const selectionContextSource = String(selectionContext?.selected_sku_source ?? '').trim().toLowerCase() || 'none'
  const selectorAnchorSource = String(selector?.anchor_source ?? '').trim().toLowerCase()
  const hasFinalReferenceImages = Array.isArray(skuReferenceImages?.sku_images)
    && skuReferenceImages.sku_images.some((item) => item && typeof item === 'object')
  const firstFinalReference = Array.isArray(skuReferenceImages?.sku_images) ? skuReferenceImages.sku_images[0] : null
  const finalReferenceIndex = firstFinalReference && typeof firstFinalReference === 'object'
    ? Number((firstFinalReference as Record<string, unknown>).original_index ?? (firstFinalReference as Record<string, unknown>).index)
    : Number.NaN
  const manualFinalReferenceLocked = hasFinalReferenceImages
    && preferredReferenceSource === 'manual'
    && preferredReferenceIndex !== null
    && selectorAnchorSource === 'manual'
    && Number.isInteger(finalReferenceIndex)
    && finalReferenceIndex === preferredReferenceIndex

  let finalReferenceSelectionSource = 'none'
  let finalReferenceSelectionMode = 'none'

  if (manualFinalReferenceLocked) {
    finalReferenceSelectionSource = 'user'
    finalReferenceSelectionMode = 'manual_locked_single'
  } else if (hasFinalReferenceImages) {
    finalReferenceSelectionSource = 'ai'
    finalReferenceSelectionMode =
      selectionContextSource === 'user' || (selectedSkuSource === 'user' && selectionContextSource === 'none')
        ? 'ai_with_user_sku_scope'
        : 'ai_auto'
  } else if (selectedSkuSource === 'user') {
    finalReferenceSelectionSource = 'pending'
    finalReferenceSelectionMode = 'pending_user_sku_scope'
  } else if (selectedSkuKey || selectedSkuLocked || selectedSkuSource !== 'none') {
    finalReferenceSelectionSource = 'pending'
    finalReferenceSelectionMode = 'pending_ai_auto'
  }

  const referenceScopeSource = selectedSkuSource === 'user'
    ? 'user_sku'
    : selectedSkuKey || selectedSkuLocked || selectedSkuSource !== 'none'
      ? 'ai_auto'
      : 'none'

  return {
    selection_mode: selectionMode,
    reference_status:
      referenceStatusFromPayload && ['idle', 'pending', 'ready', 'failed'].includes(referenceStatusFromPayload)
        ? referenceStatusFromPayload
        : hasFinalReferenceImages
          ? 'ready'
          : selectedSkuLocked
            ? 'pending'
            : 'idle',
    selected_sku: {
      key: selectedSkuKey,
      name: selectedSkuName,
      locked: selectedSkuLocked,
      source: selectedSkuSource,
    },
    reference_scope: {
      source: referenceScopeSource,
      locked: referenceScopeSource === 'user_sku' && (referenceScopeImageIds.length > 0 || referenceScopeImageIndices.length > 0),
      image_indices: referenceScopeImageIndices,
      image_ids: referenceScopeImageIds,
      image_count: referenceScopeImageIds.length > 0 ? referenceScopeImageIds.length : referenceScopeImageIndices.length,
    },
    preferred_reference_image: {
      index: preferredReferenceIndex,
      image_id: preferredReferenceImageId,
      source: preferredReferenceSource,
      locked_by_user: preferredReferenceSource === 'manual' && (preferredReferenceIndex !== null || Boolean(preferredReferenceImageId)),
    },
    final_reference_selection: {
      source: finalReferenceSelectionSource,
      mode: finalReferenceSelectionMode,
      selection_context_source: selectionContextSource,
    },
  }
}

export function derivePricingInsights(extractedData?: Record<string, any>, fallbackPrice?: number | null) {
  const prices: number[] = []
  const pushPrice = (value: unknown) => {
    const parsed = normalizeNumber(value)
    if (parsed !== null) {
      prices.push(parsed)
    }
  }

  if (extractedData?.price_range) {
    const rangeValues = String(extractedData.price_range).match(/\d+(?:\.\d+)?/g) || []
    rangeValues.map(Number).forEach((value) => {
      if (!Number.isNaN(value)) {
        prices.push(value)
      }
    })
  }

  if (Array.isArray(extractedData?.parsed_skus)) {
    extractedData.parsed_skus.forEach((sku: Record<string, any>) => {
      pushPrice(sku?.price)
    })
  }

  if (prices.length === 0 && fallbackPrice !== null && fallbackPrice !== undefined) {
    prices.push(fallbackPrice)
  }

  if (prices.length === 0) {
    return { marketAvg: null, suggested: null }
  }

  const min = Math.min(...prices)
  const max = Math.max(...prices)
  const marketAvg = Number(((min + max) / 2).toFixed(2))
  const suggested = Number((marketAvg * 1.1).toFixed(2))

  return { marketAvg, suggested }
}

export function buildAttributeList(extractedData?: Record<string, any>) {
  const attributes = extractedData?.parsed_attributes
  if (!attributes || typeof attributes !== 'object') {
    return []
  }

  const entries = Array.isArray(attributes)
    ? attributes
      .filter((item) => item && typeof item === 'object')
      .map((item) => [item.key ?? item.label ?? item.name ?? '', item.value ?? ''] as [string, string])
    : Object.entries(attributes)

  return entries
    .filter(([key, value]) => typeof value === 'string' && value.trim())
    .filter(([key]) => !/颜色|color|colour|尺码|尺寸|size/i.test(String(key)))
    .map(([key, value]) => ({ label: String(key), value: String(value) }))
}

export function buildHighlightList(extractedData?: Record<string, any>) {
  const criticalInfo = Array.isArray(extractedData?.critical_info) ? extractedData.critical_info : []
  const mapped = criticalInfo
    .filter((item) => item && typeof item === 'object')
    .map((item) => ({
      title: String(item.type ?? item.title ?? '').trim(),
      detail: String(item.content ?? item.detail ?? '').trim()
    }))
    .filter((item) => item.title && item.detail)

  if (mapped.length > 0) {
    return mapped
  }

  const attributes = extractedData?.parsed_attributes
  if (!attributes || typeof attributes !== 'object') {
    return []
  }

  const entries = Array.isArray(attributes)
    ? attributes
      .filter((item) => item && typeof item === 'object')
      .map((item) => [item.key ?? item.label ?? item.name ?? '', item.value ?? ''] as [string, string])
    : Object.entries(attributes)

  return entries
    .filter(([key, value]) => typeof value === 'string' && value.trim())
    .filter(([key]) => !/颜色|color|colour|尺码|尺寸|size/i.test(String(key)))
    .map(([key, value]) => ({ title: String(key), detail: String(value) }))
}

export function buildFlagList(extractedData?: Record<string, any>) {
  const flags = extractedData?.flags
  if (!flags || typeof flags !== 'object') {
    return []
  }

  const entries = Array.isArray(flags)
    ? flags
      .filter((item) => item && typeof item === 'object')
      .map((item) => [item.key ?? item.label ?? item.name ?? '', item.value] as [string, unknown])
    : Object.entries(flags)

  return entries
    .filter(([key, value]) => Boolean(key) && value !== undefined && value !== null)
    .map(([key, value]) => {
      let normalizedValue: boolean | string | number
      if (typeof value === 'boolean') {
        normalizedValue = value
      } else if (typeof value === 'number') {
        normalizedValue = value
      } else if (typeof value === 'string') {
        const trimmed = value.trim()
        if (trimmed.toLowerCase() === 'true') {
          normalizedValue = true
        } else if (trimmed.toLowerCase() === 'false') {
          normalizedValue = false
        } else {
          normalizedValue = trimmed
        }
      } else {
        normalizedValue = String(value)
      }

      return {
        label: humanizeKey(String(key)),
        value: normalizedValue
      }
    })
}

export function buildDimensionList(extractedData?: Record<string, any>) {
  const dimensions = extractedData?.parsed_dimensions
  if (!dimensions || typeof dimensions !== 'object') {
    return []
  }

  const entries = Array.isArray(dimensions)
    ? dimensions
      .filter((item) => item && typeof item === 'object')
      .map((item) => [item.key ?? item.label ?? item.name ?? '', item.value] as [string, unknown])
    : Object.entries(dimensions)

  return entries
    .filter(([key, value]) => Boolean(key) && value !== undefined && value !== null && value !== '')
    .map(([key, value]) => {
      const keyText = String(key)
      const numeric = normalizeNumber(value)
      const lowerKey = keyText.toLowerCase()
      let formatted = ''

      if (numeric !== null) {
        if (lowerKey.includes('weight') || keyText.includes('重量')) {
          if (lowerKey.includes('kg')) {
            formatted = formatWeight(numeric * 1000)
          } else if (lowerKey.includes('g')) {
            formatted = formatWeight(numeric)
          } else {
            formatted = formatWeight(numeric)
          }
        } else if (lowerKey.includes('mm')) {
          formatted = `${formatDimensionValue(numeric)} mm`
        } else if (lowerKey.includes('cm') || keyText.includes('厘米')) {
          formatted = `${formatDimensionValue(numeric * 10)} mm`
        } else {
          formatted = `${formatDimensionValue(numeric)} mm`
        }
      } else {
        formatted = String(value)
      }

      return {
        label: humanizeKey(stripUnitSuffix(keyText)),
        value: formatted
      }
    })
}

export function buildImageTagList(extractedData?: Record<string, any>) {
  const tags = extractedData?.image_tags
  if (tags && typeof tags === 'object') {
    return Object.entries(tags)
      .map(([key, value]) => ({ key, count: Array.isArray(value) ? value.length : 0 }))
      .filter((item) => item.count > 0)
      .map((item) => ({ label: humanizeKey(item.key), count: item.count }))
  }

  const analysis = Array.isArray(extractedData?.image_analysis) ? extractedData?.image_analysis : []
  if (!analysis) {
    return []
  }

  const counts = new Map<string, number>()
  analysis.forEach((item: Record<string, any>) => {
    const tag = item?.tag
    if (typeof tag === 'string' && tag.trim()) {
      counts.set(tag, (counts.get(tag) || 0) + 1)
    }
  })

  return Array.from(counts.entries()).map(([key, count]) => ({
    label: humanizeKey(key),
    count
  }))
}

export function buildOverviewMetrics(preview: { images?: Array<{ storage_path?: string; original_url?: string; url?: string }>; extracted_data?: Record<string, any> }) {
  const images = Array.isArray(preview?.images) ? preview.images : []
  const extractedData = preview?.extracted_data

  const fallbackImages = Array.isArray(extractedData?.images) ? extractedData.images : []
  const imageCount = images.length > 0 ? images.length : fallbackImages.length

  const attributes = extractedData?.parsed_attributes
  const attributeCount = Array.isArray(attributes)
    ? attributes.length
    : attributes && typeof attributes === 'object'
      ? Object.keys(attributes).length
      : 0

  const skus = Array.isArray(extractedData?.parsed_skus) ? extractedData.parsed_skus : []
  const skusCount = skus.length

  const rawSections = extractedData?.raw_sections
  const sectionsCount =
    rawSections && typeof rawSections === 'object'
      ? Object.values(rawSections).filter((value) => typeof value === 'string' && value.trim()).length
      : 0

  const rawTextLength = typeof extractedData?.raw_page_text === 'string' ? extractedData.raw_page_text.length : 0

  return [
    { key: 'images', value: imageCount },
    { key: 'skus', value: skusCount },
    { key: 'attributes', value: attributeCount },
    { key: 'sections', value: sectionsCount },
    { key: 'rawText', value: rawTextLength }
  ]
}

export function buildCollectStepList(status?: string) {
  const normalized = status || 'pending'
  const steps = [
    { key: 'queued', status: 'pending' },
    { key: 'assigned', status: 'pending' },
    { key: 'collecting', status: 'pending' },
    { key: 'uploading', status: 'pending' },
    { key: 'completed', status: 'pending' }
  ] as Array<{ key: string; status: 'pending' | 'active' | 'done' | 'error' }>

  const markDoneUntil = (index: number) => {
    steps.forEach((step, idx) => {
      if (idx < index) {
        step.status = 'done'
      }
    })
    if (steps[index]) {
      steps[index].status = 'active'
    }
  }

  switch (normalized) {
    case 'pending':
    case 'queued':
      steps[0].status = 'active'
      break
    case 'assigned':
      markDoneUntil(1)
      break
    case 'collecting':
      markDoneUntil(2)
      break
    case 'uploading':
      markDoneUntil(3)
      break
    case 'completed':
      steps.forEach((step) => {
        step.status = 'done'
      })
      break
    case 'failed':
    case 'cancelled':
    case 'expired':
      steps.forEach((step, idx) => {
        if (idx < steps.length - 1) {
          step.status = 'done'
        }
      })
      steps[steps.length - 1].status = 'error'
      break
    default:
      steps[0].status = 'active'
      break
  }

  return steps
}

export function resolveCollectProgress(status?: string, progress?: number | null) {
  if (typeof progress === 'number' && !Number.isNaN(progress)) {
    return Math.max(0, Math.min(100, Math.round(progress)))
  }

  switch (status) {
    case 'assigned':
      return 25
    case 'collecting':
      return 55
    case 'uploading':
      return 80
    case 'completed':
      return 100
    case 'failed':
    case 'cancelled':
    case 'expired':
      return 100
    case 'pending':
    case 'queued':
    default:
      return 10
  }
}

export function resolveCollectOptions(usePolling: boolean) {
  if (usePolling) {
    return { polling: true, raw_mode: true }
  }
  return { raw_mode: true }
}

export function shouldFallbackToPolling(errorPayload?: { detail?: { code?: string }; code?: string }) {
  const code = errorPayload?.detail?.code || errorPayload?.code || ''
  return ['PLUGIN_NOT_CONNECTED', 'NO_AVAILABLE_DEVICE'].includes(code)
}

export function buildSkuStructure(extractedData?: Record<string, any>) {
  if (!Array.isArray(extractedData?.parsed_skus)) {
    return []
  }

  return extractedData.parsed_skus
    .filter((sku: Record<string, any>) => sku && (sku.name || sku.variant_value || sku.variantValue || sku.value))
    .map((sku: Record<string, any>, index: number) => {
      const attributes =
        sku.attributes && typeof sku.attributes === 'object'
          ? Object.entries(sku.attributes)
            .filter(([_, value]) => typeof value === 'string' && value.trim())
            .map(([key, value]) => ({ label: String(key), value: String(value) }))
          : []

      return {
        name: String(sku.name || sku.variant_value || sku.variantValue || sku.value || `SKU ${index + 1}`),
        price: normalizeNumber(sku.price),
        stock: normalizeNumber(sku.stock),
        variantType: String(sku.variant_type || sku.variantType || sku.type || ''),
        variantValue: String(sku.variant_value || sku.variantValue || sku.value || ''),
        attributes
      }
    })
}

export type SkuMatrixVariant = {
  key: string
  name: string
  price: number | null
  stock: number | null
  imageUrl: string
  attributes: Record<string, string>
}

export type SkuMatrixOption = {
  value: string
  label: string
  imageUrl?: string
}

export type SkuMatrixModel = {
  variants: SkuMatrixVariant[]
  attributeOrder: string[]
  optionsByAttribute: Record<string, SkuMatrixOption[]>
  source: 'parsed' | 'raw' | 'mixed' | 'single' | 'empty'
}

export type SkuMatrixState = {
  selected: Record<string, string>
  availability: Record<string, Record<string, boolean>>
  resolvedVariant: SkuMatrixVariant | null
  matchingVariants: SkuMatrixVariant[]
  priceMin: number | null
  priceMax: number | null
}

const HTML_ENTITY_REPLACEMENTS: Record<string, string> = {
  '&lt;': '<',
  '&gt;': '>',
  '&amp;': '&',
  '&quot;': '"',
  '&#39;': "'",
}

function decodeHtmlEntities(value: string) {
  if (!value.includes('&')) {
    return value
  }
  let decoded = value
  decoded = decoded.replace(/&(lt|gt|amp|quot);/gi, (match) => HTML_ENTITY_REPLACEMENTS[match.toLowerCase()] || match)
  decoded = decoded.replace(/&#39;/gi, "'")
  decoded = decoded.replace(/&#(\d+);/g, (_, digits: string) => {
    const codePoint = Number.parseInt(digits, 10)
    return Number.isFinite(codePoint) ? String.fromCodePoint(codePoint) : `&#${digits};`
  })
  decoded = decoded.replace(/&#x([0-9a-f]+);/gi, (_, digits: string) => {
    const codePoint = Number.parseInt(digits, 16)
    return Number.isFinite(codePoint) ? String.fromCodePoint(codePoint) : `&#x${digits};`
  })
  return decoded
}

function normalizeTextValue(value: unknown) {
  if (value === null || value === undefined) {
    return ''
  }
  return decodeHtmlEntities(String(value)).trim()
}

function normalizeSkuImageUrl(value: unknown) {
  const text = normalizeTextValue(value)
  if (!text) {
    return ''
  }
  if (text.startsWith('//')) {
    return `https:${text}`
  }
  return text
}

function isSkuVariantAvailable(variant: SkuMatrixVariant) {
  if (variant.stock === null || variant.stock === undefined) {
    return true
  }
  return variant.stock > 0
}

function normalizeSelectionKey(value: string) {
  return value.replace(/^;+|;+$/g, '').trim()
}

function safeParseSkuRaw(raw: unknown): Record<string, any> | null {
  if (!raw) {
    return null
  }
  if (typeof raw === 'object' && raw !== null) {
    return raw as Record<string, any>
  }
  if (typeof raw !== 'string') {
    return null
  }
  try {
    const parsed = JSON.parse(raw)
    if (parsed && typeof parsed === 'object') {
      return parsed as Record<string, any>
    }
  } catch (error) {
    return null
  }
  return null
}

function buildParsedSkuVariants(extractedData?: Record<string, any>) {
  if (!Array.isArray(extractedData?.parsed_skus)) {
    return []
  }

  return extractedData.parsed_skus
    .map((sku: Record<string, any>, index: number): SkuMatrixVariant | null => {
      if (!sku || typeof sku !== 'object') {
        return null
      }

      const key =
        normalizeTextValue(sku.sku_id || sku.skuId || sku.id)
        || `parsed_${index + 1}`
      const name =
        normalizeTextValue(sku.name || sku.variant_value || sku.variantValue || sku.value)
        || `SKU ${index + 1}`

      const attributes: Record<string, string> = {}
      if (sku.attributes && typeof sku.attributes === 'object') {
        Object.entries(sku.attributes).forEach(([attrKey, attrValue]) => {
          const keyText = normalizeTextValue(attrKey)
          const valueText = normalizeTextValue(attrValue)
          if (!keyText || !valueText) {
            return
          }
          attributes[keyText] = valueText
        })
      }

      const variantType = normalizeTextValue(sku.variant_type || sku.variantType || sku.type)
      const variantValue = normalizeTextValue(sku.variant_value || sku.variantValue || sku.value)
      if (!Object.keys(attributes).length && variantType && variantValue) {
        attributes[variantType] = variantValue
      }

      return {
        key,
        name,
        price: normalizeNumber(sku.price),
        stock: normalizeNumber(sku.stock),
        imageUrl: normalizeSkuImageUrl(sku.image_url || sku.imageUrl || sku.image),
        attributes
      }
    })
    .filter((item): item is SkuMatrixVariant => Boolean(item))
}

function buildRawSkuVariants(extractedData?: Record<string, any>) {
  const rawSections = extractedData?.raw_sections && typeof extractedData.raw_sections === 'object'
    ? extractedData.raw_sections
    : null
  const parsedRaw = safeParseSkuRaw(rawSections?.sku_raw_text ?? extractedData?.sku_raw_text ?? extractedData?.skuRawText)
  if (!parsedRaw) {
    return []
  }

  const skuModel = parsedRaw?.skuModel && typeof parsedRaw.skuModel === 'object' ? parsedRaw.skuModel : parsedRaw
  if (!skuModel || typeof skuModel !== 'object') {
    return []
  }

  const pathMeta = new Map<string, { attrKey: string; attrValue: string; imageUrl: string }>()
  const orderedProps: Array<{
    attrKey: string
    valueSet: Set<string>
    imageByValue: Map<string, string>
  }> = []
  const orderedPropLookup = new Map<string, {
    attrKey: string
    valueSet: Set<string>
    imageByValue: Map<string, string>
  }>()
  const rawProps = Array.isArray(skuModel.skuProps) ? skuModel.skuProps : []

  rawProps.forEach((prop: Record<string, any>) => {
    if (!prop || typeof prop !== 'object') {
      return
    }
    const attrKey = normalizeTextValue(prop.prop || prop.propName || prop.name || prop.title)
    if (!attrKey) {
      return
    }
    let propMeta = orderedPropLookup.get(attrKey)
    if (!propMeta) {
      propMeta = {
        attrKey,
        valueSet: new Set<string>(),
        imageByValue: new Map<string, string>()
      }
      orderedPropLookup.set(attrKey, propMeta)
      orderedProps.push(propMeta)
    }
    const values = Array.isArray(prop.value) ? prop.value : Array.isArray(prop.values) ? prop.values : []
    values.forEach((valueItem: Record<string, any>) => {
      if (!valueItem || typeof valueItem !== 'object') {
        return
      }
      const attrValue = normalizeTextValue(valueItem.name || valueItem.value || valueItem.title)
      if (!attrValue) {
        return
      }
      propMeta.valueSet.add(attrValue)
      const imageUrl = normalizeSkuImageUrl(
        valueItem.imageUrl || valueItem.image || valueItem.imgUrl || valueItem.imageURI || valueItem.imageUri
      )
      if (imageUrl) {
        propMeta.imageByValue.set(attrValue, imageUrl)
      }
      const candidates: string[] = []
      const propPath = normalizeTextValue(valueItem.propPath || valueItem.path || valueItem.specPath)
      if (propPath) {
        candidates.push(normalizeSelectionKey(propPath))
      }
      const propId = normalizeTextValue(prop.propId || prop.id)
      const valueId = normalizeTextValue(valueItem.valueId || valueItem.id)
      if (propId && valueId) {
        candidates.push(normalizeSelectionKey(`${propId}:${valueId}`))
      }
      candidates.forEach((candidate) => {
        if (!candidate) {
          return
        }
        pathMeta.set(candidate, { attrKey, attrValue, imageUrl })
      })
    })
  })

  const parseTextAttributes = (textLike: unknown): Record<string, string> => {
    const text = normalizeTextValue(textLike)
    if (!text || !orderedProps.length) {
      return {}
    }

    let segments: string[] = [text]
    for (const separator of ['>', '＞', '/', '／', '|', '｜', ';', '；', ',', '，']) {
      if (!text.includes(separator)) {
        continue
      }
      const next = text
        .split(separator)
        .map((part) => normalizeTextValue(part))
        .filter(Boolean)
      if (next.length >= 2) {
        segments = next
        break
      }
    }

    const attributes: Record<string, string> = {}

    segments.forEach((segment) => {
      const segmentText = normalizeTextValue(segment)
      if (!segmentText) {
        return
      }
      const matched = segmentText.match(/^([^:：]+)[:：](.+)$/)
      if (!matched) {
        return
      }
      const attrToken = normalizeTextValue(matched[1])
      const valueToken = normalizeTextValue(matched[2])
      if (!attrToken || !valueToken) {
        return
      }
      const propMeta = orderedPropLookup.get(attrToken)
      if (propMeta) {
        attributes[propMeta.attrKey] = valueToken
      }
    })

    if (Object.keys(attributes).length === orderedProps.length) {
      return attributes
    }

    if (segments.length === orderedProps.length) {
      orderedProps.forEach((propMeta, index) => {
        const value = normalizeTextValue(segments[index])
        if (value) {
          attributes[propMeta.attrKey] = value
        }
      })
      if (Object.keys(attributes).length > 0) {
        return attributes
      }
    }

    segments.forEach((segment) => {
      const value = normalizeTextValue(segment)
      if (!value) {
        return
      }
      const matchedProps = orderedProps.filter((propMeta) => propMeta.valueSet.has(value))
      if (matchedProps.length === 1) {
        const targetKey = matchedProps[0].attrKey
        if (!attributes[targetKey]) {
          attributes[targetKey] = value
        }
      }
    })

    if (Object.keys(attributes).length > 0) {
      return attributes
    }

    if (orderedProps.length === 1) {
      const onlyKey = orderedProps[0]?.attrKey
      if (onlyKey) {
        return { [onlyKey]: text }
      }
    }

    return {}
  }

  const rawMap =
    (skuModel.skuMap && typeof skuModel.skuMap === 'object' ? skuModel.skuMap : null)
    || (skuModel.skuInfoMap && typeof skuModel.skuInfoMap === 'object' ? skuModel.skuInfoMap : null)
    || (skuModel.sku_map && typeof skuModel.sku_map === 'object' ? skuModel.sku_map : null)
    || (skuModel.sku_info_map && typeof skuModel.sku_info_map === 'object' ? skuModel.sku_info_map : null)

  if (!rawMap || typeof rawMap !== 'object') {
    return []
  }

  const variants: SkuMatrixVariant[] = []
  Object.entries(rawMap).forEach(([skuKey, skuInfo], index) => {
    if (!skuInfo || typeof skuInfo !== 'object') {
      return
    }
    const info = skuInfo as Record<string, any>

    const attributes: Record<string, string> = {}
    let imageUrl = normalizeSkuImageUrl(
      info.imageUrl || info.image || info.imgUrl || info.imageURI || info.imageUri
    )

    const tokens = String(skuKey).match(/\d+:\d+/g) || []
    tokens.forEach((token) => {
      const normalizedToken = normalizeSelectionKey(token)
      const meta = pathMeta.get(normalizedToken)
      if (!meta) {
        return
      }
      attributes[meta.attrKey] = meta.attrValue
      if (!imageUrl && meta.imageUrl) {
        imageUrl = meta.imageUrl
      }
    })

    if (!Object.keys(attributes).length) {
      const textAttributes = parseTextAttributes(
        info.specAttrs || info.skuName || info.name || info.title || skuKey
      )
      Object.assign(attributes, textAttributes)
    }

    if (!imageUrl && Object.keys(attributes).length) {
      orderedProps.some((propMeta) => {
        const attrValue = normalizeTextValue(attributes[propMeta.attrKey])
        if (!attrValue) {
          return false
        }
        const candidateImage = propMeta.imageByValue.get(attrValue) || ''
        if (!candidateImage) {
          return false
        }
        imageUrl = candidateImage
        return true
      })
    }

    const fallbackName = Object.values(attributes).join(' / ')
    const name = normalizeTextValue(
      info.skuName || info.specAttrs || info.name || info.title || fallbackName || skuKey
    ) || `SKU ${index + 1}`
    const key = normalizeTextValue(info.skuId || info.sku_id || info.id || skuKey) || `raw_${index + 1}`

    variants.push({
      key,
      name,
      price: normalizeNumber(info.price || info.priceText || info.discountPrice || info.discount_price),
      stock: normalizeNumber(info.canBookCount || info.stock || info.quantity),
      imageUrl,
      attributes
    })
  })

  return variants
}

function hasAttributeData(variants: SkuMatrixVariant[]) {
  return variants.some((variant) => Object.keys(variant.attributes).length > 0)
}

function mergeParsedAndRawVariants(parsedVariants: SkuMatrixVariant[], rawVariants: SkuMatrixVariant[]) {
  if (!parsedVariants.length) {
    return { variants: rawVariants, source: rawVariants.length ? ('raw' as const) : ('empty' as const) }
  }
  if (!rawVariants.length) {
    return { variants: parsedVariants, source: hasAttributeData(parsedVariants) ? ('parsed' as const) : ('single' as const) }
  }

  const parsedHasAttr = hasAttributeData(parsedVariants)
  const rawHasAttr = hasAttributeData(rawVariants)
  const merged = parsedVariants.map((variant, index) => {
    const rawMatch =
      rawVariants.find((rawVariant) => rawVariant.key === variant.key)
      || (parsedVariants.length === rawVariants.length ? rawVariants[index] : null)

    if (!rawMatch) {
      return variant
    }

    const attributes = Object.keys(variant.attributes).length ? variant.attributes : rawMatch.attributes
    return {
      ...variant,
      attributes,
      imageUrl: variant.imageUrl || rawMatch.imageUrl || '',
      price: variant.price ?? rawMatch.price,
      stock: variant.stock ?? rawMatch.stock
    }
  })

  if (!parsedHasAttr && rawHasAttr) {
    const alignedRaw = rawVariants.map((variant, index) => {
      const parsedMatch = parsedVariants[index]
      if (!parsedMatch) {
        return variant
      }
      return {
        ...variant,
        price: variant.price ?? parsedMatch.price,
        stock: variant.stock ?? parsedMatch.stock,
        imageUrl: variant.imageUrl || parsedMatch.imageUrl || ''
      }
    })
    return { variants: alignedRaw, source: 'mixed' as const }
  }

  return { variants: merged, source: parsedHasAttr ? ('parsed' as const) : ('mixed' as const) }
}

function ensurePseudoSkuAttribute(variants: SkuMatrixVariant[]) {
  if (variants.length <= 1 || hasAttributeData(variants)) {
    return variants
  }
  return variants.map((variant) => ({
    ...variant,
    attributes: { SKU: variant.name || variant.key }
  }))
}

function buildSkuOptions(variants: SkuMatrixVariant[]) {
  const attributeOrder: string[] = []
  const optionMap: Record<string, Map<string, SkuMatrixOption>> = {}

  variants.forEach((variant) => {
    Object.entries(variant.attributes).forEach(([attrKeyRaw, attrValueRaw]) => {
      const attrKey = normalizeTextValue(attrKeyRaw)
      const attrValue = normalizeTextValue(attrValueRaw)
      if (!attrKey || !attrValue) {
        return
      }
      if (!attributeOrder.includes(attrKey)) {
        attributeOrder.push(attrKey)
      }
      if (!optionMap[attrKey]) {
        optionMap[attrKey] = new Map<string, SkuMatrixOption>()
      }
      if (!optionMap[attrKey].has(attrValue)) {
        optionMap[attrKey].set(attrValue, {
          value: attrValue,
          label: attrValue,
          imageUrl: variant.imageUrl || undefined
        })
      }
    })
  })

  const optionsByAttribute: Record<string, SkuMatrixOption[]> = {}
  Object.entries(optionMap).forEach(([attrKey, valueMap]) => {
    optionsByAttribute[attrKey] = Array.from(valueMap.values())
  })

  return { attributeOrder, optionsByAttribute }
}

function variantMatchesSelection(variant: SkuMatrixVariant, selection: Record<string, string>) {
  return Object.entries(selection).every(([attrKey, attrValue]) => {
    const expected = normalizeTextValue(attrValue)
    if (!expected) {
      return true
    }
    return normalizeTextValue(variant.attributes[attrKey]) === expected
  })
}

function hasMatchingVariant(variants: SkuMatrixVariant[], selection: Record<string, string>) {
  return variants.some((variant) => variantMatchesSelection(variant, selection))
}

export function buildSkuMatrixModel(extractedData?: Record<string, any>): SkuMatrixModel {
  const parsedVariants = buildParsedSkuVariants(extractedData)
  const rawVariants = buildRawSkuVariants(extractedData)
  const mergedResult = mergeParsedAndRawVariants(parsedVariants, rawVariants)
  const variants = ensurePseudoSkuAttribute(mergedResult.variants)
  if (!variants.length) {
    return {
      variants: [],
      attributeOrder: [],
      optionsByAttribute: {},
      source: 'empty'
    }
  }

  const { attributeOrder, optionsByAttribute } = buildSkuOptions(variants)
  const source = attributeOrder.length > 0 ? mergedResult.source : 'single'

  return {
    variants,
    attributeOrder,
    optionsByAttribute,
    source
  }
}

export function computeSkuMatrixState(model: SkuMatrixModel, currentSelection: Record<string, string> = {}): SkuMatrixState {
  if (!model.variants.length) {
    return {
      selected: {},
      availability: {},
      resolvedVariant: null,
      matchingVariants: [],
      priceMin: null,
      priceMax: null
    }
  }

  const selected: Record<string, string> = {}
  const parsedSelection = currentSelection && typeof currentSelection === 'object' ? currentSelection : {}
  const hasStockVariants = model.variants.some((variant) => isSkuVariantAvailable(variant))
  const scopeVariants = hasStockVariants ? model.variants.filter((variant) => isSkuVariantAvailable(variant)) : model.variants

  model.attributeOrder.forEach((attrKey) => {
    const options = model.optionsByAttribute[attrKey] || []
    const baseSelection = { ...selected }
    const availableValuesInStock = options
      .filter((option) => hasMatchingVariant(scopeVariants, { ...baseSelection, [attrKey]: option.value }))
      .map((option) => option.value)
    const availableValuesAll = options
      .filter((option) => hasMatchingVariant(model.variants, { ...baseSelection, [attrKey]: option.value }))
      .map((option) => option.value)
    const preferredValue = normalizeTextValue(parsedSelection[attrKey])
    if (preferredValue && availableValuesAll.includes(preferredValue)) {
      selected[attrKey] = preferredValue
      return
    }
    if (availableValuesInStock.length > 0) {
      selected[attrKey] = availableValuesInStock[0]
      return
    }
    if (availableValuesAll.length > 0) {
      selected[attrKey] = availableValuesAll[0]
      return
    }
    if (options[0]?.value) {
      selected[attrKey] = options[0].value
    }
  })

  const availability: Record<string, Record<string, boolean>> = {}
  model.attributeOrder.forEach((attrKey) => {
    const options = model.optionsByAttribute[attrKey] || []
    availability[attrKey] = {}
    options.forEach((option) => {
      const probeSelection = {
        ...selected,
        [attrKey]: option.value
      }
      const matchAny = hasMatchingVariant(model.variants, probeSelection)
      availability[attrKey][option.value] = matchAny
    })
  })

  const matchingVariants = model.variants.filter((variant) => variantMatchesSelection(variant, selected))
  const preferredResolved = matchingVariants.find((variant) => isSkuVariantAvailable(variant))
  const resolvedVariant = preferredResolved || matchingVariants[0] || null

  const pricedVariants = matchingVariants.length > 0 ? matchingVariants : model.variants
  const prices = pricedVariants
    .map((variant) => variant.price)
    .filter((price): price is number => typeof price === 'number' && Number.isFinite(price))
  const priceMin = prices.length ? Math.min(...prices) : null
  const priceMax = prices.length ? Math.max(...prices) : null

  return {
    selected,
    availability,
    resolvedVariant,
    matchingVariants,
    priceMin,
    priceMax
  }
}

export function buildRawSectionList(extractedData?: Record<string, any>) {
  const sections = extractedData?.raw_sections
  if (!sections || typeof sections !== 'object') {
    return []
  }

  return Object.entries(sections)
    .filter(([_, value]) => value !== null && value !== undefined)
    .map(([key, value]) => {
      if (typeof value === 'string') {
        return { label: key, content: value }
      }
      return { label: key, content: JSON.stringify(value, null, 2) }
    })
    .filter((item) => item.content && item.content.trim())
}

const normalizeScheduleStepMinutes = (stepMinutes: number) => {
  if (!Number.isFinite(stepMinutes) || stepMinutes <= 0) {
    return 5
  }
  return Math.max(1, Math.round(stepMinutes))
}

const resolveValidDate = (value: Date, fallback: Date) => {
  if (value instanceof Date && Number.isFinite(value.getTime())) {
    return value
  }
  return fallback
}

export function roundUpToNextMinutes(input: Date, stepMinutes = 5) {
  const safeStep = normalizeScheduleStepMinutes(stepMinutes)
  const safeInput = resolveValidDate(input, new Date())
  const stepMs = safeStep * 60 * 1000
  const rounded = Math.ceil(safeInput.getTime() / stepMs) * stepMs
  return new Date(rounded)
}

export function getBrowserNowRounded(stepMinutes = 5, nowProvider?: () => Date) {
  const now = nowProvider ? nowProvider() : new Date()
  return roundUpToNextMinutes(now, stepMinutes)
}

export function coerceFutureDateTime(input: Date, stepMinutes = 5, nowProvider?: () => Date) {
  const roundedInput = roundUpToNextMinutes(input, stepMinutes)
  const roundedNow = getBrowserNowRounded(stepMinutes, nowProvider)
  if (roundedInput.getTime() < roundedNow.getTime()) {
    return roundedNow
  }
  return roundedInput
}
